
import { computed, defineComponent, reactive, toRef } from 'vue';
import { markRaw } from '@vue/runtime-core';
import _ from 'lodash';
import Utils from '@/utils/QSUtils';
import GridColumnUtils from '@/utils/GridColumnUtils';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import GridHeader from './GridHeader.vue';
import GridBody from './GridBody.vue';
import GridHeaderData from '@/models/grid/GridHeaderData';
import GridRowData from '@/models/grid/GridRowData';
import BootstrapHelper from '@/utils/bootstrapHelpers';
import { useStore } from 'vuex';
import { Actions, Getters, Mutations, store, formFieldsStoreV36, userSettingsStore, miscSettingsStore } from '@/store';
import { FieldNames } from '@/constants/FieldNames';
import LogUsageManager from '@/manager/LogUsageManager';
import OriginalValuesManager from '@/manager/3.6/OriginalValuesManager';
import CompUtils from '@/models/comp/CompUtils';
import IFeedData from '@/models/comp/IFeedData';
import Loader from '@/components/Loader.vue';
import ModalPRMisMatchAddress from '@/components/3.6/modals/PublicRecordsMisMatchAddress.vue';
import SmartExchangeClaimsManager from '@/manager/3.6/SmartExchangeClaimsManager';
import { newUpsertCompDataRequestByFieldId } from '@/store/3.6/models/IUpsertCompDataRequest';
import LocalStorageManager from '@/manager/LocalStorageManager';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import { makeGettersWatchable } from '@/utils/StoreWatch';
import SmartExchangeManager from '@/manager/3.6/SmartExchangeManager';
import LocalCompManager from '@/manager/3.6/LocalCompManager';
import HostExchangeManager from '@/manager/3.6/HostExchangeManager';
import { newUpsertCompDataRequestByFieldName } from '@/store/3.6/models/IUpsertCompDataRequest';
import { getRowVisibilityState, isAlwaysOnRowVisibilityField, normalizeRowVisibilityKey } from '@/models/grid/GridRowCellData';
import IFieldSubjectCompRelationship from '@/models/forms/IFieldSubjectCompRelationship';
import { VisibilityGrid } from '@/constants/VisibilityGrid';

export default defineComponent({
    components: {
        GridHeader,
        GridBody,
        Loader,
        ModalPRMisMatchAddress,
    },
    data() {
        const previousCompNumber = -1;

        return {
            gridHeight: '100%',
            gridWidth: '100%',
            onlyShowReportRows: true,
            onlyShowDiscrepancyRows: false,
            gridisLoading: false,
            gridNeedsRebuild: false,
            previousCompNumber,
            isLoading: false,
            modalPRMisMatchAddress: {
                show: false,
                activeComp: {},
                tabIndex: 0,
                component: markRaw(ModalPRMisMatchAddress),
            },
            pendingMisMatchActiveComp: null as any,
        };
    },
    setup() {
        const store = useStore();
        const userSettings = makeGettersWatchable(userSettingsStore);
        const miscSettings = makeGettersWatchable(miscSettingsStore);

        const formFieldsSettings = makeGettersWatchable(formFieldsStoreV36);
        const additionalFields = formFieldsStoreV36.AdditionalFields;
        const formFields = formFieldsStoreV36.FormFields;

        const computedDataSourceColumnOrder = computed(() => userSettings.DataSourceColumnOrder);
        const sourceColumnOrder = _.cloneDeep(computedDataSourceColumnOrder.value);

        const computedPreferredCasing = computed(() => userSettings.PreferredCasing);
        const preferredCasing = computedPreferredCasing.value;

        const computedDisplayDataCascadeColumn = computed(() => userSettings.DisplayDataCascadeColumn);
        const dataCascadeColumn = computedDisplayDataCascadeColumn.value;

        const computedActiveComp = computed(() => store.getters[Getters.CompAddressesV36.ActiveComp]);
        const activeComp = _.cloneDeep(computedActiveComp.value);

        const emptySources = computed(() => store.getters[Getters.CompAddressesV36.ActiveCompEmptySourceData]);
        const emptySourceData = emptySources.value;

        const computedSEDetails = computed(() => SmartExchangeClaimsManager.getCurrentClaims());
        const sEDetails = computedSEDetails.value;

        const computedFakeData = computed(() => store.getters[Getters.CompAddressesV36.GetFakeData]);
        const fakeData = computedFakeData.value;

        const originalValues = OriginalValuesManager.getPropertyByAddress(activeComp.address);

        const headerData: GridHeaderData = {
            cells: [],
        };
        const rowDatas: GridRowData[] = [];

        const state = reactive({
            activeCompData: activeComp,
            formFieldData: formFields,
            additionalFieldData: additionalFields,
            headerData: headerData,
            rowDatas: rowDatas,
            emptySourceData: emptySourceData,
            sEDetails: sEDetails,
        });

        return {
            store,
            userSettings,
            miscSettings,
            formFieldsSettings,
            activeComp: toRef(state, 'activeCompData'),
            formFields: toRef(state, 'formFieldData'),
            additionalFields: toRef(state, 'additionalFieldData'),
            headerData: toRef(state, 'headerData'),
            rowDatas: toRef(state, 'rowDatas'),
            emptySourceData: toRef(state, 'emptySourceData'),
            sEDetails: toRef(state, 'sEDetails'),
            sourceColumnOrder,
            preferredCasing,
            dataCascadeColumn,
            fakeData,
            originalValues,
        };
    },
    created() {
        // Popover document listener
        BootstrapHelper.popoverDocumentListener();

        window.addEventListener('resize', this.resizeWindowGrid);

        this.userSettings.watch('PreferredCasing', async () => {
            const result = computed(() => this.userSettings.PreferredCasing);
            this.preferredCasing = result.value;
            await this.buildGrid();
        });

        this.userSettings.watch('DisplayDataCascadeColumn', async () => {
            const result = computed(() => this.userSettings.DisplayDataCascadeColumn);
            this.dataCascadeColumn = result.value;
            await this.buildGrid();
        });

        this.userSettings.watch('DataSourceColumnOrder', async () => {
            const result = computed(() => this.userSettings.DataSourceColumnOrder);
            this.sourceColumnOrder = _.cloneDeep(result.value);
            await this.buildGrid();
        });

        this.formFieldsSettings.watch('AdditionalFields', async () => {
            const result = computed(() => this.formFieldsSettings.AdditionalFields);
            this.additionalFields = result.value;
            await this.buildGrid();
        });

        this.formFieldsSettings.watch('FormFields', async () => {
            const result = computed(() => this.formFieldsSettings.FormFields);
            this.formFields = result.value;
            await this.buildGrid();
        });

        this.store.watch(
            (state, getters) => getters[Getters.CompAddressesV36.OnlyShowReportRows],
            async () => {
                const result = computed(() => this.store.getters[Getters.CompAddressesV36.OnlyShowReportRows]);
                this.onlyShowReportRows = result.value;
                this.buildGridPayload();
            }
        );

        this.store.watch(
            (state, getters) => getters[Getters.CompAddressesV36.OnlyShowDiscrepancyRows],
            async () => {
                const result = computed(() => this.store.getters[Getters.CompAddressesV36.OnlyShowDiscrepancyRows]);
                this.onlyShowDiscrepancyRows = result.value;
                this.buildGridPayload();
            }
        );

        this.userSettings.watch('PreferredCasing', async () => {
            const result = computed(() => this.userSettings.PreferredCasing);
            this.preferredCasing = result.value;
            await this.buildGrid();
        });

        this.userSettings.watch('DisplayDataCascadeColumn', async () => {
            const result = computed(() => this.userSettings.DisplayDataCascadeColumn);
            this.dataCascadeColumn = result.value;
            await this.buildGrid();
        });

        this.userSettings.watch('DataSourceColumnOrder', async () => {
            const result = computed(() => this.userSettings.DataSourceColumnOrder);
            this.sourceColumnOrder = _.cloneDeep(result.value);
            await this.buildGrid();
        });

        this.store.watch(
            (state, getters) => getters[Getters.CompAddressesV36.ActiveComp],
            async () => {
                const computedActiveComp = computed(() => this.store.getters[Getters.CompAddressesV36.ActiveComp]);
                this.activeComp = _.cloneDeep(computedActiveComp.value);
                this.originalValues = OriginalValuesManager.getPropertyByAddress(this.activeComp.address);
                await this.buildGrid();
            },
            { deep: true }
        );

        this.store.watch(
            (state, getters) => getters[Getters.CompAddressesV36.ActiveCompEmptySourceData],
            async () => {
                const result = computed(() => store.getters[Getters.CompAddressesV36.ActiveCompEmptySourceData]);
                this.emptySourceData = result.value;
                await this.buildGrid();
            }
        );

        this.store.watch(
            (state, getters) => SmartExchangeClaimsManager.getCurrentClaims(),
            async () => {
                const result = computed(() => SmartExchangeClaimsManager.getCurrentClaims());
                this.sEDetails = result.value;
                await this.buildGrid();
            }
        );

        this.store.watch(
            (state, getters) => getters[Getters.CompAddressesV36.IsSEColumnCollasped],
            async () => {
                await this.buildGrid();
            }
        );

        this.store.watch(
            (state, getters) => getters[Getters.CompAddressesV36.IsDBColumnCollasped],
            async () => {
                await this.buildGrid();
            }
        );

        // The PR mismatch modal is deferred (see buildGrid()) while the Announcements modal is
        // open so the two don't stack - launch it once the Announcements modal closes.
        this.miscSettings.watch('AnnouncementsModalOpen', (isOpen: boolean) => {
            if (!isOpen && this.pendingMisMatchActiveComp) {
                this.launchMisMatchPRAddressModal(this.pendingMisMatchActiveComp);
                this.pendingMisMatchActiveComp = null;
            }
        });
    },
    async mounted() {
        await this.buildGrid();
        this.resizeWindowGrid();

        (this as any).eventBus.on('resizeWindowGrid', (e: any) => {
            this.resizeWindowGrid();
        });

        // Map View/Photo View dropdowns rely on applyTransferToFormForRepeatingSections() (run inside
        // buildGridPayload()) to populate repeatable-section transfer values (e.g. Dwellings-0-DWELL_STYLE).
        // That sync only acts on rows already present in rowDatas, which the "Only show report rows" filter
        // can exclude for a freshly-imported comp - temporarily lift it so the sync actually sees the row.
        (this as any).eventBus.on('rebuildActiveCompGrid', async () => {
            const previousOnlyShowReportRows = this.onlyShowReportRows;
            this.onlyShowReportRows = false;
            await this.buildGridPayload();
            this.onlyShowReportRows = previousOnlyShowReportRows;
        });

        (this as any).eventBus.on('toggleSubCompData', async (e: any) => {
            if (e.isCollapsed) {
                LogUsageManager.hideSubComps(e.initials);
            } else {
                this.isLoading = true;
                LogUsageManager.showSubComps(e.initials);
            }

            if (e.initials === SourceDataInitials.SmartExchange) {
                store.commit(Mutations.CompAddressesV36.SetSEColumnCollasped, e.isCollapsed);
                await SmartExchangeManager.fetchAndStoreSubComps();
            } else if (e.initials === SourceDataInitials.Database) {
                store.commit(Mutations.CompAddressesV36.SetDBColumnCollasped, e.isCollapsed);
                await LocalCompManager.fetchAndStoreSubComps();
            }

            this.isLoading = false;
        });
    },
    unmounted() {
        window.removeEventListener('resize', this.resizeWindowGrid);
    },
    methods: {
        async setActiveComp() {
            const result = computed(() => this.store.getters[Getters.CompAddressesV36.ActiveComp]);
            this.activeComp = _.cloneDeep(result.value);
        },
        async buildGrid() {
            // If updates arrive while a build is in progress, queue one follow-up build.
            if (this.gridisLoading) {
                this.gridNeedsRebuild = true;
                return;
            }

            this.gridisLoading = true;
            try {
                await this.setActiveComp();

                // Check if there is a PR mismatched address and no choice has been made
                const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(this.activeComp.address);
                const misMatchAddress = LocalStorageManager.getMisMatchAddress(address1AndUnitNo);

                if (misMatchAddress && misMatchAddress.usingClosestAddress === null && !HostExchangeManager.isSendingAll) {
                    if (this.miscSettings.AnnouncementsModalOpen) {
                        this.pendingMisMatchActiveComp = this.activeComp;
                    } else {
                        this.launchMisMatchPRAddressModal(this.activeComp);
                    }
                } else {
                    await this.buildHeaderPayload();
                    await this.buildGridPayload();
                    await this.initializeTooltips();

                    // buildGrid occurring multiple times as the active comp populates with data sources.
                    // On a different comp number, wait until DB & SE data are loaded to prevent repeated logging.
                    if (this.activeComp.sourceDataDB && this.activeComp.sourceDataSE && this.previousCompNumber !== this.activeComp.compNumber) {
                        LogUsageManager.loadedCompTab(this.activeComp);
                        this.previousCompNumber = this.activeComp.compNumber;
                    }
                }
            } finally {
                this.gridisLoading = false;

                if (this.gridNeedsRebuild) {
                    this.gridNeedsRebuild = false;
                    await this.buildGrid();
                }
            }
        },
        async buildHeaderPayload() {
            this.headerData = {
                cells: [],
            };

            if (this.sourceColumnOrder === undefined) return;

            // Field Data
            const fieldDataProps = GridColumnUtils.getHeaderCellData(SourceDataInitials.FieldData);
            this.headerData.cells.push(fieldDataProps);

                if (!this.activeComp.isSubject) {
                    const subjectPropertyProps = GridColumnUtils.getHeaderCellData(SourceDataInitials.SubjectProperty);
                    this.headerData.cells.push(subjectPropertyProps);
                }

            // Transfer Data
            const transferDataProps = GridColumnUtils.getHeaderCellData(SourceDataInitials.TransferData);
            this.headerData.cells.push(transferDataProps);

            // ==================================================================
            // Check if subComp is toggled on for current initials and has subComp
            // data.  If so, then append data after parent initials column
            // ==================================================================
            const isSEColumnCollasped = store.getters[Getters.CompAddressesV36.IsSEColumnCollasped];
            const isDBColumnCollasped = store.getters[Getters.CompAddressesV36.IsDBColumnCollasped];

            // Get column data based on user active/visible source columns
            for (const settingIdx in this.sourceColumnOrder) {
                // Skip source if user setting is disabled
                if (this.sourceColumnOrder[settingIdx].isHidden) continue;

                const initials = this.sourceColumnOrder[settingIdx].initials;
                const feedDataProps = GridColumnUtils.getHeaderCellData(initials);
                this.headerData.cells.push(feedDataProps);

                // When there is MLS source data we should display the MLS import name.
                if (initials.toLowerCase() === SourceDataInitials.Mls) {
                    GridColumnUtils.updateMlsHeaderName(feedDataProps, this.activeComp.sourceDataMLS);
                }

                if ((initials.toLowerCase() === SourceDataInitials.SmartExchange && !isSEColumnCollasped) || (initials.toLowerCase() === SourceDataInitials.Database && !isDBColumnCollasped)) {
                    if (this.activeComp?.subCompData && Utils.isKeyInObject(this.activeComp.subCompData, initials.toLowerCase())) {
                        const subComps = this.activeComp.subCompData[initials.toLowerCase()];
                        if (subComps.length > 0) {
                            for (const idx in subComps) {
                                const feedDataPropsSubComps = GridColumnUtils.getHeaderCellData(initials);
                                feedDataPropsSubComps.subCompIdx = Number(idx) + 2;
                                this.headerData.cells.push(feedDataPropsSubComps);
                            }
                        }
                    }
                }
            }

            if (this.dataCascadeColumn) {
                // Build column - Data Preference Settings
                const cascadeProps = GridColumnUtils.getHeaderCellData(SourceDataInitials.CascadeData);
                this.headerData.cells.push(cascadeProps);
            }
        },
        async buildGridPayload() {
            this.rowDatas = [];

            if (this.sourceColumnOrder === undefined) return;
            if (this.activeComp === undefined) return;
            if (this.formFields === undefined) return;

            let fieldsFilledInCount = 0;
            let sourceDataCounts = [];

            // Store the Field Filler Count
            store.dispatch(Actions.CompAddressesV36.ActiveCompFieldFillerCount, fieldsFilledInCount);

            const isSEColumnCollasped = store.getters[Getters.CompAddressesV36.IsSEColumnCollasped];
            const isDBColumnCollasped = store.getters[Getters.CompAddressesV36.IsDBColumnCollasped];

            // Filter fields based on isSubjectField property and current comp type
            const isSubjectComp = this.activeComp.compNumber === 0;
            const filteredFields = this.formFields.filter((field: any) => {
                const fieldName = String(field?.fieldName || '');
                // Room metadata can be supplied as ROOM_LEVEL or only as its ROOM_ROOM_TYPE
                // child. Keep either available so the Subject repeat expansion can create
                // the concatenated ROOM_LEVEL row.
                if (isSubjectComp && (fieldName === 'ROOM_LEVEL' || fieldName === 'ROOM_ROOM_TYPE')) {
                    return true;
                }

                // If no isSubjectField property is set, show the field for all comps
                if (field.isSubjectField === undefined) {
                    return true;
                }
                // Subject tab shows only fields with isSubjectField === true
                // Comp tabs show only fields with isSubjectField === false
                return isSubjectComp ? field.isSubjectField === true : field.isSubjectField === false;
            });

            // Process all fields including repeating fields
            const result = await this.processFieldsForGrid(
                filteredFields,
                isSEColumnCollasped,
                isDBColumnCollasped
            );

            this.rowDatas = result.rowDatas;
            fieldsFilledInCount = result.fieldsFilledInCount;

            await this.applyTransferToFormForRepeatingSections(this.rowDatas);

            // Store the Field Filler Count
            store.dispatch(Actions.CompAddressesV36.ActiveCompFieldFillerCount, fieldsFilledInCount);

            // Get active comps source data counts
            for (const settingIdx in this.sourceColumnOrder) {
                const initials = this.sourceColumnOrder[settingIdx].initials;
                if (initials.toLowerCase() === 'se' || initials.toLowerCase() === 'db') {
                    const result = this.getFeedData(this.activeComp, initials);
                    sourceDataCounts.push(result);
                }
            }

            store.dispatch(Actions.CompAddressesV36.ActiveCompSourceCounts, sourceDataCounts);
        },
        async processFieldsForGrid(formFields: any[], isSEColumnCollasped: boolean, isDBColumnCollasped: boolean): Promise<{ rowDatas: GridRowData[]; fieldsFilledInCount: number }> {
            const rowDatas: GridRowData[] = [];
            let fieldsFilledInCount = 0;
            // Sources can resolve two different canonical Amenity indices to the exact same
            // value (proven via [Amenities] console diagnostics: index 0 and index 1 both
            // resolved to the same value/source). Track resolved signatures per collection so
            // a genuine duplicate row can be dropped based on actual data, not index guessing.
            const amenitySignaturesByCollection = new Map<string, Set<string>>();
            // The nested BL* collections contain a label metadata field and a value field.
            // Only the value field should render as a grid row.
            let currentFormFields = formFields.filter((field: any) =>
                !this.isRepeatableBlankLabelField(String(field?.fieldName || ''))
            );

            const dwellingIndices = this.getDwellingIndicesFromPayload(this.activeComp);
            const unitIndicesByDwelling = this.getDwellingUnitIndicesByDwellingFromPayload(this.activeComp);
            const unitLevelIndicesByDwellingUnit = this.getUnitLevelIndicesByDwellingUnitFromPayload(this.activeComp);
            const unitRoomIndicesByDwellingUnit = this.getUnitRoomIndicesByDwellingUnitFromPayload(this.activeComp);
            const dwellingHeatingIndicesByDwelling = this.getDwellingHeatingIndicesByDwellingFromPayload(this.activeComp);
            const dwellingCoolingIndicesByDwelling = this.getDwellingCoolingIndicesByDwellingFromPayload(this.activeComp);
            const dwellingFoundationIndicesByDwelling = this.getDwellingFoundationIndicesByDwellingFromPayload(this.activeComp);
            const dwellingFinishedAreaRoomSummaryIndicesByDwelling = this.getDwellingFinishedAreaRoomSummaryIndicesByDwellingFromPayload(this.activeComp);
            const dwellingDefectIndicesByDwelling = this.getDwellingDefectIndicesByDwellingFromPayload(this.activeComp);
            currentFormFields = this.expandDwellingsAndUnitsByPayload(
                currentFormFields,
                dwellingIndices,
                unitIndicesByDwelling,
                unitLevelIndicesByDwellingUnit,
                unitRoomIndicesByDwellingUnit,
                dwellingHeatingIndicesByDwelling,
                dwellingCoolingIndicesByDwelling,
                dwellingFoundationIndicesByDwelling,
                dwellingFinishedAreaRoomSummaryIndicesByDwelling,
                dwellingDefectIndicesByDwelling
            );

            // Build repeat slots from payload keys like Outbuildings-0-*, Outbuildings-1-*, etc.
            const outbuildingIndices = this.getOutbuildingIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandOutbuildingFieldsByPayload(currentFormFields, outbuildingIndices);

            // Build repeat slots for nested Outbuilding collections (like Heating within Dwelling).
            const outbuildingUtilitiesIndicesByOutbuilding = this.getOutbuildingUtilitiesIndicesByOutbuildingFromPayload(this.activeComp);
            currentFormFields = this.expandOutbuildingCollectionFieldsByPayload(
                currentFormFields,
                outbuildingIndices,
                outbuildingUtilitiesIndicesByOutbuilding,
                'OutbuildingUtilities',
                ['OUTBUILDING_UTILITIES']
            );
            const mechanicalHeatSystemIndicesByOutbuilding = this.getMechanicalHeatSystemIndicesByOutbuildingFromPayload(this.activeComp);
            currentFormFields = this.expandOutbuildingCollectionFieldsByPayload(
                currentFormFields,
                outbuildingIndices,
                mechanicalHeatSystemIndicesByOutbuilding,
                'MechanicalHeatSystem',
                ['MECH_HEAT_FLATTENED', 'MECH_HEAT_SYSTEM', 'MECH_HEAT_DETAIL']
            );

            // Build repeat slots from payload keys like VehicleStorage-0-*, VehicleStorage-1-*, etc.
            const vehicleStorageIndices = this.getVehicleStorageIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandVehicleStorageFieldsByPayload(currentFormFields, vehicleStorageIndices);

            // Build repeat slots for Amenities sub-collections (OutdoorLiving-0/1, etc.) like Dwelling Heating/Cooling.
            const amenityCollectionIndices = this.getAmenityCollectionIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandAmenityFieldsByPayload(currentFormFields, amenityCollectionIndices);

            const siteStreetTypeIndices = this.getSiteStreetTypeIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandTopLevelCollectionFieldsByPayload(
                currentFormFields,
                'SiteStreetType',
                siteStreetTypeIndices,
                (fieldName: string) =>
                    fieldName === 'SIT_STREET_TYPE_SURFACE' ||
                    fieldName === 'PU_STREET_TYPE' ||
                    fieldName === 'PU_STREET_SURFACE' ||
                    fieldName === 'SITE_STREET_TYPE' ||
                    fieldName === 'PU_SITE_STREET_TYPE' ||
                    fieldName === 'PU_SITE_STREET_SURFACE'
            );

            const propertyRestrictionIndices = this.getPropertyRestrictionIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandTopLevelCollectionFieldsByPayload(
                currentFormFields,
                'PropertyRestrictions',
                propertyRestrictionIndices,
                (fieldName: string) => fieldName === 'SIT_FEAT_RSTRCTN_DTL'
            );

            const easementIndices = this.getEasementIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandTopLevelCollectionFieldsByPayload(
                currentFormFields,
                'Easements',
                easementIndices,
                (fieldName: string) => fieldName === 'SIT_FEAT_EASEMENT_DTL'
            );

            const encroachmentIndices = this.getEncroachmentIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandTopLevelCollectionFieldsByPayload(
                currentFormFields,
                'Encroachments',
                encroachmentIndices,
                (fieldName: string) =>
                    fieldName === 'SIT_FEAT_ENCROACH_DTL' ||
                    fieldName === 'PU_SITE_FEAT_ENCROACH' ||
                    fieldName === 'PU_SITE_FEAT_ENCROACH_BRD_ON_SUB' ||
                    fieldName === 'PU_SITE_FEAT_ENCROACH_BRD_ON_ADJ'
            );

            const siteFeatureIndices = this.getSiteFeatureIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandTopLevelCollectionFieldsByPayload(
                currentFormFields,
                'SiteFeatures',
                siteFeatureIndices,
                (fieldName: string) =>
                    fieldName === 'SIT_FEAT_SITCHAR_DTL' ||
                    fieldName === 'PU_SITE_FEAT_SITCHAR' ||
                    fieldName === 'PU_SITE_FEAT_SITCHAR_TOP_TYPE' ||
                    fieldName === 'PU_SITE_FEAT_SITCHAR_DRAIN_RSN'
            );

            const renewableEnergyComponentIndices = this.getRenewableEnergyComponentIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandTopLevelCollectionFieldsByPayload(
                currentFormFields,
                'RenewableEnergyComponents',
                renewableEnergyComponentIndices,
                (fieldName: string) =>
                    fieldName === 'EE_RENEWABLES' ||
                    fieldName === 'PU_EE_RENEWABLE_TYPE' ||
                    fieldName === 'PU_EE_OWNER_TYPE' ||
                    fieldName === 'PU_EE_FINANCING_ARRANGEMENT_YES'
            );

            const siteInfluenceIndices = this.getSiteInfluenceIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandTopLevelCollectionFieldsByPayload(
                currentFormFields,
                'SiteInfluence',
                siteInfluenceIndices,
                (fieldName: string) =>
                    fieldName === 'SIT_INFL' ||
                    fieldName === 'SIT_INFL_FLATTENED' ||
                    fieldName === 'SIT_INFL_INFL' ||
                    fieldName === 'PU_SITE_INFL_WTR_DTL_TYPE' ||
                    fieldName === 'PU_SITE_INFL_WTR_DTL_PRIV_ACCESS'
            );

            const viewImpairmentIndices = this.getViewImpairmentIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandTopLevelCollectionFieldsByPayload(
                currentFormFields,
                'ViewImpairment',
                viewImpairmentIndices,
                (fieldName: string) =>
                    fieldName === 'SIT_VWIMP_VIEW_FLATTENED' ||
                    fieldName === 'SIT_VWIMP_VIEW' ||
                    fieldName === 'SIT_VWIMP_RNGVW' ||
                    fieldName === 'SIT_VWIMP_IMP'
            );

            const siteDrainageIndices = this.getSiteDrainageIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandTopLevelCollectionFieldsByPayload(
                currentFormFields,
                'SiteDrainage',
                siteDrainageIndices,
                (fieldName: string) => fieldName === 'SITE_DRAINAGE'
            );

            const siteViewIndices = this.getSiteViewIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandTopLevelCollectionFieldsByPayload(
                currentFormFields,
                'SiteViews',
                siteViewIndices,
                (fieldName: string) =>
                    fieldName === 'ST_VIEW' ||
                    fieldName === 'PU_ST_VIEW' ||
                    fieldName === 'PU_ST_RANGE'
            );

            const waterFrontageIndices = this.getWaterFrontageIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandTopLevelCollectionFieldsByPayload(
                currentFormFields,
                'WaterFrontages',
                waterFrontageIndices,
                (fieldName: string) =>
                    fieldName === 'WATER_FRONTAGE' ||
                    fieldName === 'PU_WATER_FRONTAGE_TYPE' ||
                    fieldName === 'PU_WATER_FRONTAGE_NAME' ||
                    fieldName === 'PU_WATER_FRONTAGE_ACCESS_DEPTH'
            );

            const buildingCertificationIndices = this.getBuildingCertificationIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandTopLevelCollectionFieldsByPayload(
                currentFormFields,
                'BuildingCertifications',
                buildingCertificationIndices,
                (fieldName: string) =>
                    fieldName === 'GREEN_BUILD_CERT' ||
                    fieldName === 'PU_GREEN_BUILD_CERT_DESC' ||
                    fieldName === 'PU_GREEN_BUILD_CERT_RATE' ||
                    fieldName === 'EE_CERTS_ORGANIZATION' ||
                    fieldName === 'EE_CERTS_ORGANIZATION_FLATTENED' ||
                    fieldName === 'EE_CERTS' ||
                    fieldName === 'EE_YEAR' ||
                    fieldName === 'EE_VERSION' ||
                    fieldName === 'EE_RATING'
            );

            const efficiencyRatingIndices = this.getEfficiencyRatingIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandTopLevelCollectionFieldsByPayload(
                currentFormFields,
                'EfficiencyRatings',
                efficiencyRatingIndices,
                (fieldName: string) =>
                    fieldName === 'GREEN_EFF_RATING' ||
                    fieldName === 'PU_GREEN_EFF_RATING' ||
                    fieldName === 'PU_GREEN_EFF_SCORE' ||
                    fieldName === 'EE_GREEN_EFF' ||
                    fieldName === 'EE_GREEN_EFF_FLATTENED' ||
                    fieldName === 'EE_GREEN_RATING' ||
                    fieldName === 'EE_GREEN_SCORE'
            );

            const saleListingInformationIndices = this.getSaleListingInformationIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandSaleListingInformationFieldsByPayload(
                currentFormFields,
                saleListingInformationIndices
            );

            const OwnersIndices = this.getOwnersIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandOwnersFieldsByPayload(currentFormFields, OwnersIndices);

            const assessorParcelIndices = this.getAssessorParcelIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandAssessorParcelFieldsByPayload(currentFormFields, assessorParcelIndices);

            const transferHistoryIndices = this.getTransferHistoryIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandTransferHistoryFieldsByPayload(currentFormFields, transferHistoryIndices);

            const ExteriorQualitiesIndices = this.getExteriorQualitiesIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandSectionCollectionFieldsByPayload(currentFormFields, 'ExteriorQualities', ExteriorQualitiesIndices, (sectionName: string) => this.isExteriorQualitiesSection(sectionName));

            const exteriorConditionIndices = this.getExteriorConditionIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandSectionCollectionFieldsByPayload(currentFormFields, 'ExteriorConditions', exteriorConditionIndices, (sectionName: string) => this.isExteriorConditionSection(sectionName));

            const interiorQualityIndices = this.getInteriorQualityIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandSectionCollectionFieldsByPayload(currentFormFields, 'InteriorQualities', interiorQualityIndices, (sectionName: string) => this.isInteriorQualitySection(sectionName));

            const interiorConditionIndices = this.getInteriorConditionIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandSectionCollectionFieldsByPayload(currentFormFields, 'InteriorConditions', interiorConditionIndices, (sectionName: string) => this.isInteriorConditionSection(sectionName));

            const ADUQualitiesIndices = this.getADUQualitiesIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandSectionCollectionFieldsByPayload(currentFormFields, 'ADUQualities', ADUQualitiesIndices, (sectionName: string) => this.isADUQualitiesSection(sectionName));

            const aduConditionsIndices = this.getADUConditionsIndicesFromPayload(this.activeComp);
            currentFormFields = this.expandSectionCollectionFieldsByPayload(currentFormFields, 'ADUConditions', aduConditionsIndices, (sectionName: string) => this.isADUConditionsSection(sectionName));

            // Use an index-based loop so dynamic insertions (repeating clones) do not skip trailing fields.
            for (let fieldIdx = 0; fieldIdx < currentFormFields.length; fieldIdx++) {
                const field = currentFormFields[fieldIdx];
                const fieldId = field.fieldId;
                const fieldName = field.fieldName;
                
                const payloadFieldName = this.getRepeatingPayloadFieldName(
                    field,
                    dwellingIndices,
                    unitIndicesByDwelling,
                    unitLevelIndicesByDwellingUnit,
                    unitRoomIndicesByDwellingUnit,
                    dwellingHeatingIndicesByDwelling,
                    dwellingCoolingIndicesByDwelling,
                    dwellingFoundationIndicesByDwelling,
                    dwellingFinishedAreaRoomSummaryIndicesByDwelling,
                    dwellingDefectIndicesByDwelling,
                    outbuildingIndices,
                    vehicleStorageIndices,
                    amenityCollectionIndices,
                    siteStreetTypeIndices,
                    propertyRestrictionIndices,
                    easementIndices,
                    encroachmentIndices,
                    siteFeatureIndices,
                    renewableEnergyComponentIndices,
                    siteInfluenceIndices,
                    viewImpairmentIndices,
                    siteDrainageIndices,
                    siteViewIndices,
                    waterFrontageIndices,
                    buildingCertificationIndices,
                    efficiencyRatingIndices,
                    saleListingInformationIndices,
                    OwnersIndices,
                    assessorParcelIndices,
                    transferHistoryIndices,
                    ExteriorQualitiesIndices,
                    exteriorConditionIndices,
                    interiorQualityIndices,
                    interiorConditionIndices,
                    ADUQualitiesIndices,
                    aduConditionsIndices
                );
                const friendlyFieldName = field.friendlyFieldName;
                const suggestFieldRule = field.suggestFieldRule;
                const fieldCasing = field.preferredCasing || this.preferredCasing;
                const flattenedDataPriorities = field.flattenedDataPriorities;
                let transferValue = '';
                let transferInitial = '';
                let transferValueMatchSourcesData = true;

                // Calculate updated fieldId based on indices in payloadFieldName
                // ALWAYS recalculate from fieldName indices, not from field.fieldId
                const updatedFieldId = this.getUpdatedFieldIdForRepeatingField(fieldId, payloadFieldName);
                const transferOriginalValue = this.originalValues?.find((f: any) => f.fieldId === updatedFieldId)
                    ? this.originalValues.find((f: any) => f.fieldId === updatedFieldId)?.originalValue
                    : '';
                
                const rowData: GridRowData = {
                    fieldName: payloadFieldName,
                    fieldId: updatedFieldId,
                    photoRow: payloadFieldName === FieldNames.CompPhoto,
                    cells: [],
                    sectionName: field.sectionName,
                };

                // Field Data
                const fieldDataProps = GridColumnUtils.getRowCellData(SourceDataInitials.FieldData);
                fieldDataProps.value = friendlyFieldName;
                rowData.cells.push(fieldDataProps);

                    if (!this.activeComp.isSubject) {
                        const subjectPropertyProps = GridColumnUtils.getRowCellData(SourceDataInitials.SubjectProperty);
                        subjectPropertyProps.value = this.getSubjectPropertyValue(payloadFieldName);
                        rowData.cells.push(subjectPropertyProps);
                    }

                // Transfer Data
                const transferDataProps = GridColumnUtils.getRowCellData(SourceDataInitials.TransferData);
                const compData = this.getTransferCompData(payloadFieldName, updatedFieldId);
                transferDataProps.value = compData ? compData.value : '';

                if (fieldName !== FieldNames.CompPhoto) {
                    // Apply casing from global level or Field Level
                    transferDataProps.value = GridColumnUtils.transferValueCasing(transferDataProps.value, fieldCasing);
                }

                transferDataProps.value = GridColumnUtils.formatData(transferDataProps.value, fieldName);

                let fieldRulesApplied = compData ? compData.fieldRulesApplied : false;

                transferDataProps.fieldRulesApplied = fieldRulesApplied;

                transferDataProps.sourceInitial = compData ? compData.sourceInitial : '';
                transferDataProps.fieldNotes = this.activeComp.notesData && this.activeComp.notesData.find((f: any) => f.fieldName === payloadFieldName) ? this.activeComp.notesData.find((f: any) => f.fieldName === payloadFieldName).value : '';
                transferDataProps.friendlyFieldName = friendlyFieldName;
                transferDataProps.suggestFieldRule = suggestFieldRule;
                const transferInitialIdx = compData ? compData.sourceInitialIdx : undefined;

                // If rules has been applied to a blank value, then set the sourceInitial to be User Override on loading of the grid
                if (transferDataProps.fieldRulesApplied && transferDataProps.sourceInitial === '') {
                    transferDataProps.sourceInitial = SourceDataInitials.UserOverride;

                    const request = newUpsertCompDataRequestByFieldId(this.activeComp, updatedFieldId);
                    request.sourceInitial = SourceDataInitials.UserOverride;
                    request.description = 'DataGrid.buildGridPayload()';
                    store.commit(Mutations.CompAddressesV36.UpsertCompSourceDataByFieldId, request);
                }

                transferValue = transferDataProps.value;
                transferInitial = transferDataProps.sourceInitial;

                rowData.cells.push(transferDataProps);

                // Fields Filled In Count
                if (transferDataProps.sourceInitial !== 'ff' && transferValue) {
                    fieldsFilledInCount++;
                }

                // Get column data based on user active/visible source columns
                for (const settingIdx in this.sourceColumnOrder) {
                    // Skip source if user setting is disabled
                    if (this.sourceColumnOrder[settingIdx].isHidden) continue;

                    const initials = this.sourceColumnOrder[settingIdx].initials;
                    const normalizedInitials = String(initials || '').toUpperCase();
                    const normalizedEmptySources = (this.emptySourceData || []).map((src: string) => String(src || '').toUpperCase());
                    const isSkeletonEligibleSource = ['DB', 'MLS', 'SE', 'PR'].includes(normalizedInitials);
                    const feedDataProps = GridColumnUtils.getRowCellData(initials);

                    let sourceFeedData: any = null;
                    if (normalizedInitials === 'DB') {
                        sourceFeedData = this.activeComp?.sourceDataDB;
                    } else if (normalizedInitials === 'MLS') {
                        sourceFeedData = this.activeComp?.sourceDataMLS;
                    } else if (normalizedInitials === 'SE') {
                        sourceFeedData = this.activeComp?.sourceDataSE;
                    } else if (normalizedInitials === 'PR') {
                        sourceFeedData = this.activeComp?.sourceDataPR;
                    }

                    const isSourcePendingLoad = isSkeletonEligibleSource && !sourceFeedData;

                    let loadFakeData = false;
                    let isPendingSourceData = false;
                    if (initials === 'SE') {
                        if (!this.sEDetails.isOptedIn && fieldName !== FieldNames.CompPhoto) {
                            loadFakeData = true;
                        } else if (isSourcePendingLoad && fieldName !== FieldNames.CompPhoto) {
                            loadFakeData = true;
                            isPendingSourceData = true;
                        }
                    } else {
                        if (fieldName !== FieldNames.CompPhoto && isSkeletonEligibleSource) {
                            if (isSourcePendingLoad) {
                                loadFakeData = true;
                                isPendingSourceData = true;
                            } else if (normalizedInitials === 'MLS' && normalizedEmptySources.includes(normalizedInitials)) {
                                // DB and PR resolve to a blank cell when empty; only MLS gets blurred out.
                                loadFakeData = true;
                            }
                        }
                    }

                    // Source Data is blank / empty.  Inject dummy value to blur out
                    if (loadFakeData) {
                        let value = '';
                        if (this.fakeData[fieldName]) {
                            value = this.fakeData[fieldName];
                        } else {
                            value = this.generateFakeData(transferValue);
                        }

                        feedDataProps.value = value;
                        feedDataProps.sourceInitial = initials;
                        feedDataProps.isFakeData = true;
                        feedDataProps.isPendingSourceData = isPendingSourceData;
                    } else {
                        const result = this.processFeedDataValue({
                            value: this.getFeedDataValue(this.activeComp, initials, payloadFieldName),
                            initials: initials,
                            fieldName: fieldName,
                            transferValue: transferValue,
                            transferOriginalValue: transferOriginalValue,
                            transferInitial: transferInitial,
                            transferInitialIdx: transferInitialIdx,
                            isSubCompColumnData: false,
                        });

                        if (!result.dataMatch && result.value) {
                            transferValueMatchSourcesData = false;
                        }

                        // Insert Assign Additional Fields text for PR column cell row if missing
                        // Assign Additional Fields setting
                        if (fieldName.includes('BL_BLANK') && initials.toLowerCase() === 'pr') {
                            const isDataPointsEmpty = this.additionalFields && this.additionalFields.additionalFields?.find((f: any) => f.blankFieldId === fieldId) ? this.additionalFields.additionalFields.find((f: any) => f.blankFieldId === fieldId)?.dataPoints : null;
                            if (!isDataPointsEmpty) {
                                result.value = 'Assign Additional Fields';
                            }
                        }

                        feedDataProps.value = result.value;
                        feedDataProps.dataMatch = result.dataMatch;
                        feedDataProps.sourceInitial = result.sourceInitial;
                        feedDataProps.dataMatch = result.dataMatch;
                        feedDataProps.dataSelected = result.dataSelected;
                        if (result.dataSelected && transferInitialIdx !== undefined && transferInitialIdx !== null) {
                            feedDataProps.sourceInitialIdx = Number(transferInitialIdx);
                        }
                    }
                    rowData.cells.push(feedDataProps);

                    // ==================================================================
                    // Check if subComp is toggled on for current initials and has subComp
                    // data.  If so, then append data after parent initials column
                    // ==================================================================
                    if ((initials.toLowerCase() === 'se' && !isSEColumnCollasped) || (initials.toLowerCase() === 'db' && !isDBColumnCollasped)) {
                        if (this.activeComp?.subCompData?.[initials.toLowerCase()]) {
                            const subComps = this.activeComp?.subCompData?.[initials.toLowerCase()];
                            if (subComps.length > 0) {
                                for (const idx in subComps) {
                                    const subCompFieldValue = subComps?.[idx]?.[fieldName];
                                    const feedDataPropsSubComps = GridColumnUtils.getRowCellData(initials);
                                    const sourceInitialIdx = Number(idx);
                                    const resultSubComp = this.processFeedDataValue({
                                        value: subCompFieldValue,
                                        initials: initials,
                                        fieldName: fieldName,
                                        transferValue: transferValue,
                                        transferOriginalValue: transferOriginalValue,
                                        transferInitial: transferInitial,
                                        transferInitialIdx: transferInitialIdx,
                                        sourceInitialIdx: sourceInitialIdx,
                                        isSubCompColumnData: true,
                                    });

                                    if (!resultSubComp.dataMatch && resultSubComp.value) {
                                        transferValueMatchSourcesData = false;
                                    }

                                    feedDataPropsSubComps.value = resultSubComp.value;
                                    feedDataPropsSubComps.dataMatch = resultSubComp.dataMatch;
                                    feedDataPropsSubComps.sourceInitial = resultSubComp.sourceInitial;
                                    feedDataPropsSubComps.dataMatch = resultSubComp.dataMatch;
                                    feedDataPropsSubComps.dataSelected = resultSubComp.dataSelected;
                                    feedDataPropsSubComps.sourceInitialIdx = sourceInitialIdx;

                                    rowData.cells.push(feedDataPropsSubComps);
                                }
                            }
                        }
                    }
                }

                if (this.dataCascadeColumn) {
                    // Data Preference Settings
                    const cascadeDataProps = GridColumnUtils.getRowCellData(SourceDataInitials.CascadeData);
                    cascadeDataProps.value = GridColumnUtils.styleFlattenedDataPriorities(flattenedDataPriorities);
                    rowData.cells.push(cascadeDataProps);
                }

                // For any field stuck with 'pr' as transfer source, check whether a
                // higher-priority source has a value and correct the selection.
                // This handles flat non-repeatable fields (e.g. AD_ADDR1, DT_SALTIM1)
                // that priorities cannot re-evaluate once sourceInitial is set.
                // Compound/indexed fields (Dwellings-0-*, Outbuildings-0-*, etc.) are handled
                // by applyTransferToFormForRepeatingSections — skip this correction for them.
                const normalizedTransferInitialIdx =
                    transferInitialIdx === undefined || transferInitialIdx === null || transferInitialIdx === ''
                        ? undefined
                        : Number(transferInitialIdx);

                const isCompoundPayloadFieldName = /^[A-Za-z]+-\d+-/.test(payloadFieldName);

                if (
                    !isCompoundPayloadFieldName &&
                    transferInitial &&
                    String(transferInitial).toLowerCase() === SourceDataInitials.PublicRecords &&
                    normalizedTransferInitialIdx === undefined &&
                    compData &&
                    String(compData.sourceInitial || '').toLowerCase() !== SourceDataInitials.UserOverride &&
                    !compData.manuallyConfirmed
                ) {
                    const getSourcePriorityFlat = (sourceInitialVal: string): number => {
                        const norm = String(sourceInitialVal || '').toLowerCase();
                        const idx = this.sourceColumnOrder.findIndex((s: any) => String(s?.initials || '').toLowerCase() === norm);
                        return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
                    };
                    const currentPriority = getSourcePriorityFlat(transferInitial);
                    // Consider any non-fake non-empty source cell with higher priority. Exclude by
                    // stable cell.initials type (not position) - non-subject comps insert an extra
                    // Subject Property column, which would otherwise shift Transfer into range.
                    const higherPriorityCells = rowData.cells.filter((cell: any) =>
                        cell?.initials !== SourceDataInitials.FieldData &&
                        cell?.initials !== SourceDataInitials.SubjectProperty &&
                        cell?.initials !== SourceDataInitials.TransferData &&
                        cell?.initials !== SourceDataInitials.CascadeData &&
                        cell?.sourceInitial &&
                        !cell?.isFakeData &&
                        String(cell.value || '').trim() !== '' &&
                        getSourcePriorityFlat(cell.sourceInitial) < currentPriority
                    );
                    const bestCell = higherPriorityCells.sort(
                        (a: any, b: any) => getSourcePriorityFlat(a.sourceInitial) - getSourcePriorityFlat(b.sourceInitial)
                    )[0];

                    if (bestCell) {
                        const bestInitial = String(bestCell.sourceInitial || '').toLowerCase();
                        const bestValue = bestCell.value ?? '';
                        const correctionRequest = newUpsertCompDataRequestByFieldName(this.activeComp, payloadFieldName);
                        correctionRequest.fieldId = updatedFieldId;
                        correctionRequest.sourceInitial = bestInitial;
                        correctionRequest.value = bestValue;
                        correctionRequest.description = 'DataGrid.buildGridPayload() PR lock-in correction';
                        store.commit(Mutations.CompAddressesV36.UpsertCompSourceDataByFieldName, correctionRequest);
                    }
                }

                // Report rows filter: only include rows whose field is part of the report's
                // original row visibility list. Combines with the discrepancy filter so both
                // checkboxes can narrow the grid together or independently.
                    const rowVisibilityReport = formFieldsStoreV36.RowVisibilityReport ?? {};
                    const isInReportList = Object.prototype.hasOwnProperty.call(
                            rowVisibilityReport,
                        normalizeRowVisibilityKey(payloadFieldName)
                    );
                    // Rows explicitly toggled hidden should stay excluded even if they're part of the report list.
                    const isExplicitlyHidden = getRowVisibilityState(
                        payloadFieldName,
                        formFieldsStoreV36.RowVisibilityGrid
                    ) === VisibilityGrid.Hidden;
                    const isReportRow = this.activeComp.isSubject || !this.onlyShowReportRows ||
                        isAlwaysOnRowVisibilityField(payloadFieldName) ||
                        (isInReportList && !isExplicitlyHidden);
                const isDiscrepancyRow = !this.onlyShowDiscrepancyRows || !transferValueMatchSourcesData;

                if (isReportRow && isDiscrepancyRow) {
                    const amenityCollectionMatch = payloadFieldName.match(/^Amenities-\d+-(\w+)-\d+-(AMENITY_[A-Z_]+)$/);
                    let isDuplicateAmenityRow = false;

                    if (amenityCollectionMatch) {
                        const amenityGroupKey = `${amenityCollectionMatch[1]}-${amenityCollectionMatch[2]}`;
                        const rowSignature = JSON.stringify(
                            rowData.cells
                                .filter((cell: any) => !cell?.isFakeData && String(cell?.value ?? '').trim() !== '')
                                .map((cell: any) => `${cell.sourceInitial}:${cell.value}`)
                                .sort()
                        );

                        if (!amenitySignaturesByCollection.has(amenityGroupKey)) {
                            amenitySignaturesByCollection.set(amenityGroupKey, new Set<string>());
                        }
                        const seenSignatures = amenitySignaturesByCollection.get(amenityGroupKey)!;
                        if (rowSignature !== '[]' && seenSignatures.has(rowSignature)) {
                            isDuplicateAmenityRow = true;
                        } else {
                            seenSignatures.add(rowSignature);
                        }
                    }

                    if (!isDuplicateAmenityRow) {
                        rowDatas.push(rowData);
                    }
                }

            }

            return {
                rowDatas: rowDatas,
                fieldsFilledInCount: fieldsFilledInCount,
            };
        },
        getOutbuildingIndicesFromPayload(activeComp: any): number[] {
            this.normalizeOutbuildingIndicesByType(activeComp);
            return this.getRepeatIndicesFromPayload(activeComp, /^Outbuildings-(\d+)-/);
        },
        getCompsForIndices(activeComp: any): any[] {
            const compsList = [activeComp];
            if (activeComp && !activeComp.isSubject) {
                const allComps = this.store.getters[Getters.CompAddressesV36.Comps] as any[];
                const subjectComp = allComps?.find(
                    (comp: any) => comp?.isSubject || String(comp?.compType || '').toLowerCase() === 'subject' || comp?.compNumber === 0
                );
                if (subjectComp && subjectComp !== activeComp) {
                    compsList.push(subjectComp);
                }
            }
            return compsList;
        },
        normalizeOutbuildingIndicesByType(activeComp: any): void {
            const keyRegex = /^Outbuildings-(\d+)-(.+)$/;
            const canonicalTypeToIndex = new Map<string, number>();
            let nextCanonicalIndex = 0;

            const getTypeKey = (value: any): string => String(value ?? '').trim().toLowerCase();

            const remapOutbuildingEntries = (
                entries: any[],
                getKey: (entry: any) => string,
                getValue: (entry: any) => any,
                setKey: (entry: any, newKey: string) => void
            ): void => {
                const rowsByIndex = new Map<number, Map<string, any>>();

                for (const entry of entries) {
                    const key = getKey(entry);
                    const match = key.match(keyRegex);
                    if (!match) {
                        continue;
                    }

                    const index = parseInt(match[1]);
                    const fieldSuffix = match[2];
                    if (!rowsByIndex.has(index)) {
                        rowsByIndex.set(index, new Map<string, any>());
                    }
                    rowsByIndex.get(index)!.set(fieldSuffix, entry);
                }

                if (rowsByIndex.size === 0) {
                    return;
                }

                const sortedSourceIndices = Array.from(rowsByIndex.keys()).sort((a, b) => a - b);
                for (const sourceIndex of sortedSourceIndices) {
                    const rowEntries = rowsByIndex.get(sourceIndex);
                    if (!rowEntries) {
                        continue;
                    }

                    const outbuildingTypeEntry = rowEntries.get('OUTBUILDING_TYPE');
                    const typeKey = getTypeKey(outbuildingTypeEntry ? getValue(outbuildingTypeEntry) : '');

                    let targetIndex = sourceIndex;
                    if (typeKey) {
                        if (!canonicalTypeToIndex.has(typeKey)) {
                            canonicalTypeToIndex.set(typeKey, nextCanonicalIndex++);
                        }
                        targetIndex = canonicalTypeToIndex.get(typeKey)!;
                    }

                    if (targetIndex === sourceIndex) {
                        continue;
                    }

                    for (const [fieldSuffix, entry] of rowEntries.entries()) {
                        setKey(entry, `Outbuildings-${targetIndex}-${fieldSuffix}`);
                    }
                }
            };

            const getSourceDataObjects = (): Record<string, any>[] => {
                const sourceDataObjects: Record<string, any>[] = [];

                if (!Array.isArray(this.sourceColumnOrder)) {
                    return sourceDataObjects;
                }

                for (const setting of this.sourceColumnOrder) {
                    const initials = setting?.initials;
                    if (!initials) {
                        continue;
                    }

                    const sourceData = CompUtils.getDataSourceSourceData(activeComp, initials);
                    if (!sourceData || Array.isArray(sourceData) || typeof sourceData !== 'object') {
                        continue;
                    }

                    sourceDataObjects.push(sourceData as Record<string, any>);
                }

                return sourceDataObjects;
            };

            const sourceDataObjects = getSourceDataObjects();
            for (const sourceData of sourceDataObjects) {
                const entries = Object.entries(sourceData).map(([key, value]) => ({ key, value }));
                remapOutbuildingEntries(
                    entries,
                    (entry) => entry.key,
                    (entry) => entry.value,
                    (entry, newKey) => {
                        entry.key = newKey;
                    }
                );

                const keysToDelete = Object.keys(sourceData).filter((key) => keyRegex.test(key));
                for (const key of keysToDelete) {
                    delete sourceData[key];
                }

                for (const entry of entries) {
                    sourceData[entry.key] = entry.value ?? '';
                }
            }

            if (Array.isArray(activeComp?.sourceData)) {
                remapOutbuildingEntries(
                    activeComp.sourceData,
                    (entry: any) => String(entry?.fieldName || ''),
                    (entry: any) => entry?.value,
                    (entry: any, newKey: string) => {
                        entry.fieldName = newKey;
                    }
                );
            }
        },
        getDwellingIndicesFromPayload(activeComp: any): number[] {
            const indices = this.getRepeatIndicesFromPayload(activeComp, /^Dwellings-(\d+)-/);
            if (indices.includes(0)) {
                return indices;
            }

            const hasRootDwellingCollection = (fieldNames: string[]): boolean =>
                fieldNames.some((fieldName) => /^(Heating|Cooling|Foundations|FinishedAreaRoomSummary|Defects)-\d+-/.test(fieldName));

            const compsList = this.getCompsForIndices(activeComp);
            let hasDwellingZero = false;
            for (const comp of compsList) {
                if (Array.isArray(comp?.sourceData) && hasRootDwellingCollection(
                    comp.sourceData
                        .map((item: any) => item?.fieldName)
                        .filter((fieldName: any) => typeof fieldName === 'string')
                )) {
                    hasDwellingZero = true;
                    break;
                }

                if (!hasDwellingZero && Array.isArray(this.sourceColumnOrder)) {
                    hasDwellingZero = this.sourceColumnOrder.some((setting: any) => {
                        const sourceData = CompUtils.getDataSourceSourceData(comp, setting?.initials);
                        return sourceData && !Array.isArray(sourceData) && typeof sourceData === 'object'
                            ? hasRootDwellingCollection(Object.keys(sourceData))
                            : false;
                    });
                    if (hasDwellingZero) break;
                }
            }

            return hasDwellingZero ? [0, ...indices].sort((a, b) => a - b) : indices;
        },
        getDwellingHeatingIndicesByDwellingFromPayload(activeComp: any): Record<number, number[]> {
            return this.getDwellingCollectionIndicesByDwellingFromPayload(activeComp, 'Heating');
        },
        getDwellingCoolingIndicesByDwellingFromPayload(activeComp: any): Record<number, number[]> {
            return this.getDwellingCollectionIndicesByDwellingFromPayload(activeComp, 'Cooling');
        },
        getDwellingFoundationIndicesByDwellingFromPayload(activeComp: any): Record<number, number[]> {
            return this.getDwellingCollectionIndicesByDwellingFromPayload(activeComp, 'Foundations');
        },
        getDwellingFinishedAreaRoomSummaryIndicesByDwellingFromPayload(activeComp: any): Record<number, number[]> {
            return this.getDwellingCollectionIndicesByDwellingFromPayload(activeComp, 'FinishedAreaRoomSummary');
        },
        getDwellingDefectIndicesByDwellingFromPayload(activeComp: any): Record<number, number[]> {
            return this.getDwellingCollectionIndicesByDwellingFromPayload(activeComp, 'Defects');
        },
        getDwellingCollectionIndicesByDwellingFromPayload(activeComp: any, collectionName: string): Record<number, number[]> {
            const result: Record<number, Set<number>> = {};
            const regex = new RegExp(`^Dwellings-(\\d+)-${collectionName}-(\\d+)-`);
            const rootCollectionRegex = new RegExp(`^${collectionName}-(\\d+)-`);

            const appendMatches = (fieldNames: string[]): void => {
                for (const fieldName of fieldNames) {
                    const match = fieldName.match(regex);
                    const rootCollectionMatch = fieldName.match(rootCollectionRegex);
                    if (!match && !rootCollectionMatch) {
                        continue;
                    }

                    // Subject sources can expose dwelling collections at the payload root
                    // (for example Heating-0-*) instead of below Dwellings-0-*.
                    const dwellingIndex = match ? parseInt(match[1]) : 0;
                    const collectionIndex = match ? parseInt(match[2]) : parseInt(rootCollectionMatch![1]);

                    if (!result[dwellingIndex]) {
                        result[dwellingIndex] = new Set<number>();
                    }

                    result[dwellingIndex].add(collectionIndex);
                }
            };

            const compsList = this.getCompsForIndices(activeComp);
            for (const comp of compsList) {
                if (Array.isArray(comp?.sourceData)) {
                    appendMatches(
                        comp.sourceData
                            .map((item: any) => item?.fieldName)
                            .filter((fieldName: any) => typeof fieldName === 'string')
                    );
                }

                if (Array.isArray(this.sourceColumnOrder)) {
                    for (const setting of this.sourceColumnOrder) {
                        const initials = setting?.initials;
                        if (!initials) {
                            continue;
                        }

                        const sourceData = CompUtils.getDataSourceSourceData(comp, initials);
                        if (!sourceData || Array.isArray(sourceData) || typeof sourceData !== 'object') {
                            continue;
                        }

                        appendMatches(Object.keys(sourceData));
                    }
                }
            }

            const sortedResult: Record<number, number[]> = {};
            for (const [dwellingIndexString, collectionIndexSet] of Object.entries(result)) {
                const dwellingIndex = parseInt(dwellingIndexString);
                sortedResult[dwellingIndex] = Array.from(collectionIndexSet).sort((a, b) => a - b);
            }

            return sortedResult;
        },
        getVehicleStorageIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^VehicleStorage-(\d+)-/);
        },
        getSiteStreetTypeIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^SiteStreetType-(\d+)-/);
        },
        getPropertyRestrictionIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^PropertyRestrictions-(\d+)-/);
        },
        getEasementIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^Easements-(\d+)-/);
        },
        getEncroachmentIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^Encroachments-(\d+)-/);
        },
        getSiteFeatureIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^SiteFeatures-(\d+)-/);
        },
        getRenewableEnergyComponentIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^RenewableEnergyComponents-(\d+)-/);
        },
        getSiteInfluenceIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^SiteInfluence-(\d+)-/);
        },
        getViewImpairmentIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^ViewImpairment-(\d+)-/);
        },
        getSiteDrainageIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^SiteDrainage-(\d+)-/);
        },
        getSiteViewIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^SiteViews-(\d+)-/);
        },
        getWaterFrontageIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^WaterFrontages-(\d+)-/);
        },
        getBuildingCertificationIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^BuildingCertifications-(\d+)-/);
        },
        getEfficiencyRatingIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^EfficiencyRatings-(\d+)-/);
        },
        getSaleListingInformationIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^SaleListingInformation-(\d+)-/);
        },
        getOwnersIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^Owners-(\d+)-/);
        },
        getAssessorParcelIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^AssessorParcels-(\d+)-/);
        },
        getTransferHistoryIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^TransferHistories-(\d+)-/);
        },
        getExteriorQualitiesIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^ExteriorQualities-(\d+)-/);
        },
        getExteriorConditionIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^ExteriorConditions-(\d+)-/);
        },
        getInteriorQualityIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^InteriorQualities-(\d+)-/);
        },
        getInteriorConditionIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^InteriorConditions-(\d+)-/);
        },
        getADUQualitiesIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^ADUQualities-(\d+)-/);
        },
        getADUConditionsIndicesFromPayload(activeComp: any): number[] {
            return this.getRepeatIndicesFromPayload(activeComp, /^ADUConditions-(\d+)-/);
        },
        getOutbuildingUtilitiesIndicesByOutbuildingFromPayload(activeComp: any): Record<number, number[]> {
            return this.getOutbuildingCollectionIndicesByOutbuildingFromPayload(activeComp, 'OutbuildingUtilities');
        },
        getMechanicalHeatSystemIndicesByOutbuildingFromPayload(activeComp: any): Record<number, number[]> {
            return this.getOutbuildingCollectionIndicesByOutbuildingFromPayload(activeComp, 'MechanicalHeatSystem');
        },
        getOutbuildingCollectionIndicesByOutbuildingFromPayload(activeComp: any, collectionName: string): Record<number, number[]> {
            const result: Record<number, Set<number>> = {};
            const regex = new RegExp(`^Outbuildings-(\\d+)-${collectionName}-(\\d+)-`);

            const appendMatches = (fieldNames: string[]): void => {
                for (const fieldName of fieldNames) {
                    const match = fieldName.match(regex);
                    if (!match) continue;
                    const outbuildingIndex = parseInt(match[1]);
                    const utilityIndex = parseInt(match[2]);
                    if (!result[outbuildingIndex]) result[outbuildingIndex] = new Set<number>();
                    result[outbuildingIndex].add(utilityIndex);
                }
            };

            const compsList = this.getCompsForIndices(activeComp);
            for (const comp of compsList) {
                if (Array.isArray(comp?.sourceData)) {
                    appendMatches(
                        comp.sourceData
                            .map((item: any) => item?.fieldName)
                            .filter((fn: any) => typeof fn === 'string')
                    );
                }

                if (Array.isArray(this.sourceColumnOrder)) {
                    for (const setting of this.sourceColumnOrder) {
                        const initials = setting?.initials;
                        if (!initials) continue;
                        const sourceData = CompUtils.getDataSourceSourceData(comp, initials);
                        if (!sourceData || Array.isArray(sourceData) || typeof sourceData !== 'object') continue;
                        appendMatches(Object.keys(sourceData));
                    }
                }
            }

            const sortedResult: Record<number, number[]> = {};
            for (const [idxStr, set] of Object.entries(result)) {
                sortedResult[parseInt(idxStr)] = Array.from(set).sort((a, b) => a - b);
            }
            return sortedResult;
        },
        getAmenityCollectionIndicesFromPayload(activeComp: any, logDuplicates = true): Record<string, number[]> {
            // Each source (DB, MLS, SE, PR, transfer) numbers its own Amenities entries
            // independently, so the same real-world entry can sit at index 0 in one source
            // and index 1 in another. Counting the raw index union would create a phantom
            // blank row for every mismatch. Instead the row count per collection is the
            // largest number of POPULATED entries found within any single source, and
            // getFeedDataValue() maps that canonical index back to each source's own Nth
            // populated entry.
            const regex = /^Amenities-\d+-(\w+)-(\d+)-/;
            const maxPopulatedCountByCollection: Record<string, number> = {};
            const payloadSnapshotByCollection: Record<string, any[]> = {};

            const hasValue = (value: any): boolean => value !== null && value !== undefined && String(value).trim() !== '';

            const countPopulatedIndices = (fieldNames: string[], source: string, compLabel: string, valueOf: (fieldName: string) => any): void => {
                const populatedIndicesByCollection: Record<string, Set<number>> = {};

                for (const fieldName of fieldNames) {
                    const match = fieldName.match(regex);
                    if (!match) {
                        continue;
                    }
                    const collectionName = match[1];
                    const collectionIndex = parseInt(match[2]);
                    const value = valueOf(fieldName);
                    if (!hasValue(value)) {
                        continue;
                    }

                    if (!populatedIndicesByCollection[collectionName]) {
                        populatedIndicesByCollection[collectionName] = new Set<number>();
                    }
                    populatedIndicesByCollection[collectionName].add(collectionIndex);

                    if (!payloadSnapshotByCollection[collectionName]) {
                        payloadSnapshotByCollection[collectionName] = [];
                    }
                    payloadSnapshotByCollection[collectionName].push({ source, compLabel, fieldName, collectionIndex, value });
                }

                for (const [collectionName, indices] of Object.entries(populatedIndicesByCollection)) {
                    maxPopulatedCountByCollection[collectionName] = Math.max(
                        maxPopulatedCountByCollection[collectionName] || 0,
                        indices.size
                    );
                }
            };

            const compsList = this.getCompsForIndices(activeComp);
            for (const comp of compsList) {
                const compLabel = comp?.isSubject ? 'Subject' : `Comp ${comp?.compNumber}`;

                // Intentionally do NOT count comp.sourceData (the already-saved Transfer
                // values) toward the row count. Once a stale duplicate row gets written to
                // Transfer by an older/buggy pass, it would otherwise keep re-justifying its
                // own second row on every subsequent grid rebuild. Row count is driven only
                // by the raw per-source feeds (DB/MLS/SE/PR) below.

                if (Array.isArray(this.sourceColumnOrder)) {
                    for (const setting of this.sourceColumnOrder) {
                        const initials = setting?.initials;
                        if (!initials) {
                            continue;
                        }
                        const sourceData = CompUtils.getDataSourceSourceData(comp, initials);
                        if (!sourceData || Array.isArray(sourceData) || typeof sourceData !== 'object') {
                            continue;
                        }
                        countPopulatedIndices(
                            Object.keys(sourceData),
                            initials,
                            compLabel,
                            (fieldName) => sourceData[fieldName]
                        );
                    }
                }

                // FormFiller-entered entries (e.g. a manually added Amenity) exist ONLY in
                // comp.sourceData - they never appear in any raw DB/MLS/SE/PR feed - so the
                // "raw feeds only" rule above would otherwise permanently hide them. Count
                // only 'ff'-sourced rows here (not 'pr') so this can't resurrect the stale
                // duplicate-index rows the comment above guards against.
                if (Array.isArray(comp?.sourceData)) {
                    const formFillerFieldNames = comp.sourceData
                        .filter((item: any) => String(item?.sourceInitial || '').toLowerCase() === SourceDataInitials.FormFiller)
                        .map((item: any) => item?.fieldName)
                        .filter((fieldName: any): fieldName is string => typeof fieldName === 'string');
                    countPopulatedIndices(
                        formFillerFieldNames,
                        SourceDataInitials.FormFiller,
                        compLabel,
                        (fieldName) => comp.sourceData.find((item: any) => item?.fieldName === fieldName)?.value
                    );
                }
            }

            // The Subject's Property Amenities section uses generic PU_SPA_AMENITY_CATEGORY/
            // PU_SPA_AMENITY_TYPE repeatable pairs rather than the Comp's discrete per-category
            // fields, so a Subject entry never naturally counts toward the loop above. Ensure a
            // Comp row exists for every Subject entry so its value has somewhere to display.
            const subjectAmenityCountByCollection: Record<string, number> = {};
            for (const entry of this.getSubjectAmenityCategoryEntries()) {
                subjectAmenityCountByCollection[entry.collectionName] = (subjectAmenityCountByCollection[entry.collectionName] || 0) + 1;
            }
            for (const [collectionName, count] of Object.entries(subjectAmenityCountByCollection)) {
                maxPopulatedCountByCollection[collectionName] = Math.max(maxPopulatedCountByCollection[collectionName] || 0, count);
            }

            const sortedResult: Record<string, number[]> = {};
            for (const [collectionName, maxPopulatedCount] of Object.entries(maxPopulatedCountByCollection)) {
                const canonicalCount = Math.max(maxPopulatedCount, 1);
                sortedResult[collectionName] = Array.from({ length: canonicalCount }, (_, i) => i);
            }
            return sortedResult;
        },
        getAmenityCollectionNameForField(fieldName: string): string | null {
            if (fieldName.includes('AMENITY_CATEGORY')) return 'AmenityCategory';
            if (fieldName.includes('OUTDOOR_ACCESSORIES')) return 'OutdoorAccessories';
            if (fieldName.includes('OUTDOOR_LIVING')) return 'OutdoorLiving';
            if (fieldName.includes('WATER_FEATURE')) return 'WaterFeatures';
            if (fieldName.includes('WHOLE_HOME')) return 'WholeHome';
            if (fieldName.includes('AMENITY_MISC')) return 'Miscellaneous';
            return null;
        },
        // Maps the Subject's free-text PU_SPA_AMENITY_CATEGORY value to the same collection
        // names used for the Comp's discrete Amenities rows (see getAmenityCollectionNameForField).
        getAmenityCollectionNameForCategoryLabel(label: string | undefined): string | null {
            switch (String(label || '').trim().toLowerCase()) {
                case 'outdoor accessories': return 'OutdoorAccessories';
                case 'outdoor living': return 'OutdoorLiving';
                case 'water features': return 'WaterFeatures';
                case 'whole home': return 'WholeHome';
                case 'miscellaneous': return 'Miscellaneous';
                default: return null;
            }
        },
        // Parses the Subject's repeatable PU_SPA_AMENITY_CATEGORY/PU_SPA_AMENITY_TYPE pairs into
        // an ordered list per matched collection, so entry N for a category maps to Comp row N.
        getSubjectAmenityCategoryEntries(): { collectionName: string; type: string }[] {
            const comps = this.store.getters[Getters.CompAddressesV36.Comps] as any[];
            const subjectComp = comps?.find(
                (comp: any) => comp?.isSubject || String(comp?.compType || '').toLowerCase() === 'subject' || comp?.compNumber === 0
            );
            const subjectSourceData = subjectComp?.sourceData;
            if (!Array.isArray(subjectSourceData)) {
                return [];
            }

            // Supports all 3 shapes the Subject may use for repeat index N: bare field name
            // (index 0 only), a direct "-N" suffix on the field name, or fully nested under
            // Amenities-0-AmenityCategory-N-*.
            const categoryRegex = /^(?:Amenities-\d+-AmenityCategory-(\d+)-)?PU_SPA_AMENITY_CATEGORY(?:-(\d+))?$/;
            const typeRegex = /^(?:Amenities-\d+-AmenityCategory-(\d+)-)?PU_SPA_AMENITY_TYPE(?:-(\d+))?$/;
            const entriesByIndex = new Map<number, { category?: string; type?: string }>();

            for (const item of subjectSourceData) {
                const fieldName = String(item?.fieldName || '');
                const categoryMatch = fieldName.match(categoryRegex);
                const typeMatch = fieldName.match(typeRegex);

                if (categoryMatch) {
                    const index = parseInt(categoryMatch[1] ?? categoryMatch[2] ?? '0');
                    const entry = entriesByIndex.get(index) || {};
                    entry.category = String(item?.value ?? '').trim();
                    entriesByIndex.set(index, entry);
                } else if (typeMatch) {
                    const index = parseInt(typeMatch[1] ?? typeMatch[2] ?? '0');
                    const entry = entriesByIndex.get(index) || {};
                    entry.type = String(item?.value ?? '').trim();
                    entriesByIndex.set(index, entry);
                }
            }

            const result: { collectionName: string; type: string }[] = [];
            // The Subject API can send stale duplicate PU_SPA_AMENITY_CATEGORY/TYPE atomic
            // values for entries beyond the first (confirmed via [SubjectAmenities] diagnostic:
            // entry 1 repeated entry 0's exact category+type even though its own flattened
            // SPA_AMENITY_CATEGORY field showed different real data). Drop exact repeats so a
            // known-stale duplicate doesn't get shown twice.
            const seenSignatures = new Set<string>();
            for (const index of Array.from(entriesByIndex.keys()).sort((a, b) => a - b)) {
                const entry = entriesByIndex.get(index);
                const collectionName = this.getAmenityCollectionNameForCategoryLabel(entry?.category);
                if (!collectionName || !entry?.type) {
                    continue;
                }

                const signature = `${entry.category}|${entry.type}`;
                if (seenSignatures.has(signature)) {
                    continue;
                }
                seenSignatures.add(signature);

                result.push({ collectionName, type: entry.type });
            }

            if (this.activeComp?.compNumber === 1) {
                const rawMatches = subjectSourceData
                    .filter((item: any) => String(item?.fieldName || '').includes('SPA_AMENITY_CATEGORY') || String(item?.fieldName || '').includes('SPA_AMENITY_TYPE'))
                    .map((item: any) => ({ fieldName: item?.fieldName, value: item?.value }));
            }

            return result;
        },
        // Returns the Subject's Nth matched value for a given Amenities collection (0-based,
        // in the order the Subject's category entries were populated).
        getSubjectAmenityValue(collectionName: string, collectionIndex: number): string {
            const matchingEntries = this.getSubjectAmenityCategoryEntries().filter((entry) => entry.collectionName === collectionName);
            return matchingEntries[collectionIndex]?.type ?? '';
        },
        getDwellingUnitIndicesByDwellingFromPayload(activeComp: any): Record<number, number[]> {
            const result: Record<number, Set<number>> = {};
            const regex = /^Dwellings-(\d+)-Units-(\d+)-/;

            const appendMatches = (fieldNames: string[]): void => {
                for (const fieldName of fieldNames) {
                    const match = fieldName.match(regex);
                    if (!match) {
                        continue;
                    }

                    const dwellingIndex = parseInt(match[1]);
                    const unitIndex = parseInt(match[2]);

                    if (!result[dwellingIndex]) {
                        result[dwellingIndex] = new Set<number>();
                    }

                    result[dwellingIndex].add(unitIndex);
                }
            };

            const compsList = this.getCompsForIndices(activeComp);
            for (const comp of compsList) {
                if (Array.isArray(comp?.sourceData)) {
                    appendMatches(
                        comp.sourceData
                            .map((item: any) => item?.fieldName)
                            .filter((fieldName: any) => typeof fieldName === 'string')
                    );
                }

                if (Array.isArray(this.sourceColumnOrder)) {
                    for (const setting of this.sourceColumnOrder) {
                        const initials = setting?.initials;
                        if (!initials) {
                            continue;
                        }

                        const sourceData = CompUtils.getDataSourceSourceData(comp, initials);
                        if (!sourceData || Array.isArray(sourceData) || typeof sourceData !== 'object') {
                            continue;
                        }

                        appendMatches(Object.keys(sourceData));
                    }
                }
            }

            const sortedResult: Record<number, number[]> = {};
            for (const [dwellingIndexString, unitIndexSet] of Object.entries(result)) {
                const dwellingIndex = parseInt(dwellingIndexString);
                sortedResult[dwellingIndex] = Array.from(unitIndexSet).sort((a, b) => a - b);
            }

            return sortedResult;
        },
        getUnitLevelIndicesByDwellingUnitFromPayload(activeComp: any): Record<number, Record<number, number[]>> {
            return this.getUnitCollectionIndicesByDwellingUnitFromPayload(activeComp, 'Levels');
        },
        getUnitRoomIndicesByDwellingUnitFromPayload(activeComp: any): Record<number, Record<number, number[]>> {
            return this.getUnitCollectionIndicesByDwellingUnitFromPayload(activeComp, 'Rooms');
        },
        getUnitCollectionIndicesByDwellingUnitFromPayload(activeComp: any, collectionName: string): Record<number, Record<number, number[]>> {
            const result: Record<number, Record<number, Set<number>>> = {};
            const regex = new RegExp(`^Dwellings-(\\d+)-Units-(\\d+)-${collectionName}-(\\d+)-`);

            const appendMatches = (fieldNames: string[]): void => {
                for (const fieldName of fieldNames) {
                    const match = fieldName.match(regex);
                    if (!match) {
                        continue;
                    }

                    const dwellingIndex = parseInt(match[1]);
                    const unitIndex = parseInt(match[2]);
                    const collectionIndex = parseInt(match[3]);

                    if (!result[dwellingIndex]) {
                        result[dwellingIndex] = {};
                    }

                    if (!result[dwellingIndex][unitIndex]) {
                        result[dwellingIndex][unitIndex] = new Set<number>();
                    }

                    result[dwellingIndex][unitIndex].add(collectionIndex);
                }
            };

            const compsList = this.getCompsForIndices(activeComp);
            for (const comp of compsList) {
                if (Array.isArray(comp?.sourceData)) {
                    appendMatches(
                        comp.sourceData
                            .map((item: any) => item?.fieldName)
                            .filter((fieldName: any) => typeof fieldName === 'string')
                    );
                }

                if (Array.isArray(this.sourceColumnOrder)) {
                    for (const setting of this.sourceColumnOrder) {
                        const initials = setting?.initials;
                        if (!initials) {
                            continue;
                        }

                        const sourceData = CompUtils.getDataSourceSourceData(comp, initials);
                        if (!sourceData || Array.isArray(sourceData) || typeof sourceData !== 'object') {
                            continue;
                        }

                        appendMatches(Object.keys(sourceData));
                    }
                }
            }

            const sortedResult: Record<number, Record<number, number[]>> = {};
            for (const [dwellingIndexString, units] of Object.entries(result)) {
                const dwellingIndex = parseInt(dwellingIndexString);
                sortedResult[dwellingIndex] = {};

                for (const [unitIndexString, collectionIndexSet] of Object.entries(units)) {
                    const unitIndex = parseInt(unitIndexString);
                    sortedResult[dwellingIndex][unitIndex] = Array.from(collectionIndexSet).sort((a, b) => a - b);
                }
            }

            return sortedResult;
        },
        getRepeatIndicesFromPayload(activeComp: any, repeatRegex: RegExp): number[] {
            const indices = new Set<number>();
            const compsList = this.getCompsForIndices(activeComp);

            for (const comp of compsList) {
                // Transfer payload (array form)
                if (Array.isArray(comp?.sourceData)) {
                    for (const item of comp.sourceData) {
                        const fieldName = item?.fieldName;
                        const match = typeof fieldName === 'string' ? fieldName.match(repeatRegex) : null;
                        if (match) {
                            indices.add(parseInt(match[1]));
                        }
                    }
                }

                // Source payloads (object form) by configured initials
                if (Array.isArray(this.sourceColumnOrder)) {
                    for (const setting of this.sourceColumnOrder) {
                        const initials = setting?.initials;
                        if (!initials) {
                            continue;
                        }

                        const sourceData = CompUtils.getDataSourceSourceData(comp, initials);
                        if (!sourceData || Array.isArray(sourceData) || typeof sourceData !== 'object') {
                            continue;
                        }

                        for (const fieldName of Object.keys(sourceData)) {
                            const match = fieldName.match(repeatRegex);
                            if (match) {
                                indices.add(parseInt(match[1]));
                            }
                        }
                    }
                }
            }

            return Array.from(indices).sort((a, b) => a - b);
        },
        getRepeatingPayloadFieldName(
            field: any,
            dwellingIndices: number[],
            unitIndicesByDwelling: Record<number, number[]>,
            unitLevelIndicesByDwellingUnit: Record<number, Record<number, number[]>>,
            unitRoomIndicesByDwellingUnit: Record<number, Record<number, number[]>>,
            dwellingHeatingIndicesByDwelling: Record<number, number[]>,
            dwellingCoolingIndicesByDwelling: Record<number, number[]>,
            dwellingFoundationIndicesByDwelling: Record<number, number[]>,
            dwellingFinishedAreaRoomSummaryIndicesByDwelling: Record<number, number[]>,
            dwellingDefectIndicesByDwelling: Record<number, number[]>,
            outbuildingIndices: number[],
            vehicleStorageIndices: number[],
            amenityCollectionIndices: Record<string, number[]>,
            siteStreetTypeIndices: number[],
            propertyRestrictionIndices: number[],
            easementIndices: number[],
            encroachmentIndices: number[],
            siteFeatureIndices: number[],
            renewableEnergyComponentIndices: number[],
            siteInfluenceIndices: number[],
            viewImpairmentIndices: number[],
            siteDrainageIndices: number[],
            siteViewIndices: number[],
            waterFrontageIndices: number[],
            buildingCertificationIndices: number[],
            efficiencyRatingIndices: number[],
            saleListingInformationIndices: number[],
            OwnersIndices: number[],
            assessorParcelIndices: number[],
            transferHistoryIndices: number[],
            ExteriorQualitiesIndices: number[],
            exteriorConditionIndices: number[],
            interiorQualityIndices: number[],
            interiorConditionIndices: number[],
            ADUQualitiesIndices: number[],
            aduConditionsIndices: number[]
        ): string {
            const dwellingPayloadFieldName = this.getDwellingPayloadFieldName(
                field,
                dwellingIndices,
                unitIndicesByDwelling,
                unitLevelIndicesByDwellingUnit,
                unitRoomIndicesByDwellingUnit,
                dwellingHeatingIndicesByDwelling,
                dwellingCoolingIndicesByDwelling,
                dwellingFoundationIndicesByDwelling,
                dwellingFinishedAreaRoomSummaryIndicesByDwelling,
                dwellingDefectIndicesByDwelling
            );
            const outbuildingPayloadFieldName = this.getOutbuildingPayloadFieldName(
                {
                    ...field,
                    fieldName: dwellingPayloadFieldName,
                },
                outbuildingIndices
            );

            const vehicleStoragePayloadFieldName = this.getVehicleStoragePayloadFieldName(
                {
                    ...field,
                    fieldName: outbuildingPayloadFieldName,
                },
                vehicleStorageIndices
            );

            const amenityPayloadFieldName = this.getAmenityPayloadFieldName(
                {
                    ...field,
                    fieldName: vehicleStoragePayloadFieldName,
                },
                amenityCollectionIndices
            );

            const siteStreetTypePayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: amenityPayloadFieldName,
                },
                'SiteStreetType',
                siteStreetTypeIndices,
                (baseFieldName: string) =>
                    baseFieldName === 'SIT_STREET_TYPE_SURFACE' ||
                    baseFieldName === 'PU_STREET_TYPE' ||
                    baseFieldName === 'PU_STREET_SURFACE' ||
                    baseFieldName === 'SITE_STREET_TYPE' ||
                    baseFieldName === 'PU_SITE_STREET_TYPE' ||
                    baseFieldName === 'PU_SITE_STREET_SURFACE'
            );

            const propertyRestrictionPayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: siteStreetTypePayloadFieldName,
                },
                'PropertyRestrictions',
                propertyRestrictionIndices,
                (baseFieldName: string) => baseFieldName === 'SIT_FEAT_RSTRCTN_DTL'
            );

            const easementPayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: propertyRestrictionPayloadFieldName,
                },
                'Easements',
                easementIndices,
                (baseFieldName: string) => baseFieldName === 'SIT_FEAT_EASEMENT_DTL'
            );

            const encroachmentPayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: easementPayloadFieldName,
                },
                'Encroachments',
                encroachmentIndices,
                (baseFieldName: string) =>
                    baseFieldName === 'SIT_FEAT_ENCROACH_DTL' ||
                    baseFieldName === 'PU_SITE_FEAT_ENCROACH' ||
                    baseFieldName === 'PU_SITE_FEAT_ENCROACH_BRD_ON_SUB' ||
                    baseFieldName === 'PU_SITE_FEAT_ENCROACH_BRD_ON_ADJ'
            );

            const siteFeaturePayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: encroachmentPayloadFieldName,
                },
                'SiteFeatures',
                siteFeatureIndices,
                (baseFieldName: string) =>
                    baseFieldName === 'SIT_FEAT_SITCHAR_DTL' ||
                    baseFieldName === 'PU_SITE_FEAT_SITCHAR' ||
                    baseFieldName === 'PU_SITE_FEAT_SITCHAR_TOP_TYPE' ||
                    baseFieldName === 'PU_SITE_FEAT_SITCHAR_DRAIN_RSN'
            );

            const renewableEnergyComponentPayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: siteFeaturePayloadFieldName,
                },
                'RenewableEnergyComponents',
                renewableEnergyComponentIndices,
                (baseFieldName: string) =>
                    baseFieldName === 'EE_RENEWABLES' ||
                    baseFieldName === 'PU_EE_RENEWABLE_TYPE' ||
                    baseFieldName === 'PU_EE_OWNER_TYPE' ||
                    baseFieldName === 'PU_EE_FINANCING_ARRANGEMENT_YES'
            );

            const siteInfluencePayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: renewableEnergyComponentPayloadFieldName,
                },
                'SiteInfluence',
                siteInfluenceIndices,
                (baseFieldName: string) =>
                    baseFieldName === 'SIT_INFL' ||
                    baseFieldName === 'SIT_INFL_FLATTENED' ||
                    baseFieldName === 'SIT_INFL_INFL' ||
                    baseFieldName === 'PU_SITE_INFL_WTR_DTL_TYPE' ||
                    baseFieldName === 'PU_SITE_INFL_WTR_DTL_PRIV_ACCESS'
            );

            const viewImpairmentPayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: siteInfluencePayloadFieldName,
                },
                'ViewImpairment',
                viewImpairmentIndices,
                (baseFieldName: string) =>
                    baseFieldName === 'SIT_VWIMP_VIEW_FLATTENED' ||
                    baseFieldName === 'SIT_VWIMP_VIEW' ||
                    baseFieldName === 'SIT_VWIMP_RNGVW' ||
                    baseFieldName === 'SIT_VWIMP_IMP'
            );

            const siteDrainagePayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: viewImpairmentPayloadFieldName,
                },
                'SiteDrainage',
                siteDrainageIndices,
                (baseFieldName: string) => baseFieldName === 'SITE_DRAINAGE'
            );

            const siteViewPayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: siteDrainagePayloadFieldName,
                },
                'SiteViews',
                siteViewIndices,
                (baseFieldName: string) =>
                    baseFieldName === 'ST_VIEW' ||
                    baseFieldName === 'PU_ST_VIEW' ||
                    baseFieldName === 'PU_ST_RANGE'
            );

            const waterFrontagePayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: siteViewPayloadFieldName,
                },
                'WaterFrontages',
                waterFrontageIndices,
                (baseFieldName: string) =>
                    baseFieldName === 'WATER_FRONTAGE' ||
                    baseFieldName === 'PU_WATER_FRONTAGE_TYPE' ||
                    baseFieldName === 'PU_WATER_FRONTAGE_NAME' ||
                    baseFieldName === 'PU_WATER_FRONTAGE_ACCESS_DEPTH'
            );

            const buildingCertificationPayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: waterFrontagePayloadFieldName,
                },
                'BuildingCertifications',
                buildingCertificationIndices,
                (baseFieldName: string) =>
                    baseFieldName === 'GREEN_BUILD_CERT' ||
                    baseFieldName === 'PU_GREEN_BUILD_CERT_DESC' ||
                    baseFieldName === 'PU_GREEN_BUILD_CERT_RATE' ||
                    baseFieldName === 'EE_CERTS_ORGANIZATION' ||
                    baseFieldName === 'EE_CERTS_ORGANIZATION_FLATTENED' ||
                    baseFieldName === 'EE_CERTS' ||
                    baseFieldName === 'EE_YEAR' ||
                    baseFieldName === 'EE_VERSION' ||
                    baseFieldName === 'EE_RATING'
            );

            const efficiencyRatingPayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: buildingCertificationPayloadFieldName,
                },
                'EfficiencyRatings',
                efficiencyRatingIndices,
                (baseFieldName: string) =>
                    baseFieldName === 'GREEN_EFF_RATING' ||
                    baseFieldName === 'PU_GREEN_EFF_RATING' ||
                    baseFieldName === 'PU_GREEN_EFF_SCORE' ||
                    baseFieldName === 'EE_GREEN_EFF' ||
                    baseFieldName === 'EE_GREEN_EFF_FLATTENED' ||
                    baseFieldName === 'EE_GREEN_RATING' ||
                    baseFieldName === 'EE_GREEN_SCORE'
            );

            const saleListingInformationPayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: efficiencyRatingPayloadFieldName,
                },
                'SaleListingInformation',
                saleListingInformationIndices,
                (baseFieldName: string) =>
                    baseFieldName === 'SLI_LIST_STATUS' ||
                    baseFieldName === 'SLI_LIST_CUR_OR_FINAL_LP'
            );

            const OwnersPayloadFieldName = this.getOwnersPayloadFieldName(
                {
                    ...field,
                    fieldName: saleListingInformationPayloadFieldName,
                },
                OwnersIndices
            );

            const assessorParcelPayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: OwnersPayloadFieldName,
                },
                'AssessorParcels',
                assessorParcelIndices,
                (baseFieldName: string) => this.isAssessorParcelField(baseFieldName)
            );

            const transferHistoryPayloadFieldName = this.getTopLevelCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: assessorParcelPayloadFieldName,
                },
                'TransferHistories',
                transferHistoryIndices,
                (baseFieldName: string) => this.isTransferHistoryField(baseFieldName)
            );

            const ExteriorQualitiesPayloadFieldName = this.getSectionCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: transferHistoryPayloadFieldName,
                },
                'ExteriorQualities',
                ExteriorQualitiesIndices,
                (sectionName: string) => this.isExteriorQualitiesSection(sectionName)
            );

            const exteriorConditionPayloadFieldName = this.getSectionCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: ExteriorQualitiesPayloadFieldName,
                },
                'ExteriorConditions',
                exteriorConditionIndices,
                (sectionName: string) => this.isExteriorConditionSection(sectionName)
            );

            const interiorQualityPayloadFieldName = this.getSectionCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: exteriorConditionPayloadFieldName,
                },
                'InteriorQualities',
                interiorQualityIndices,
                (sectionName: string) => this.isInteriorQualitySection(sectionName)
            );

            const interiorConditionPayloadFieldName = this.getSectionCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: interiorQualityPayloadFieldName,
                },
                'InteriorConditions',
                interiorConditionIndices,
                (sectionName: string) => this.isInteriorConditionSection(sectionName)
            );

            const ADUQualitiesPayloadFieldName = this.getSectionCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: interiorConditionPayloadFieldName,
                },
                'ADUQualities',
                ADUQualitiesIndices,
                (sectionName: string) => this.isADUQualitiesSection(sectionName)
            );

            return this.getSectionCollectionPayloadFieldName(
                {
                    ...field,
                    fieldName: ADUQualitiesPayloadFieldName,
                },
                'ADUConditions',
                aduConditionsIndices,
                (sectionName: string) => this.isADUConditionsSection(sectionName)
            );
        },
        isOwnersField(fieldName: string): boolean {
            return fieldName.startsWith('SUBCUROWNER');
        },
        isAssessorParcelField(fieldName: string): boolean {
            return fieldName === 'APN' || fieldName.startsWith('PU_APN');
        },
        isTransferHistoryField(fieldName: string): boolean {
            return fieldName.startsWith('TRANSFER_HISTORY_') || fieldName.startsWith('DFE_TRANSFER_HISTORY_');
        },
        normalizeSectionName(sectionName: string): string {
            return String(sectionName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        },
        isExteriorQualitiesSection(sectionName: string): boolean {
            const normalized = this.normalizeSectionName(sectionName);
            return normalized.startsWith('ExteriorQualities');
        },
        isExteriorConditionSection(sectionName: string): boolean {
            const normalized = this.normalizeSectionName(sectionName);
            return normalized.startsWith('ExteriorConditions');
        },
        isInteriorQualitySection(sectionName: string): boolean {
            const normalized = this.normalizeSectionName(sectionName);
            return normalized.startsWith('InteriorQualities');
        },
        isInteriorConditionSection(sectionName: string): boolean {
            const normalized = this.normalizeSectionName(sectionName);
            return normalized.startsWith('InteriorConditions');
        },
        isADUQualitiesSection(sectionName: string): boolean {
            const normalized = this.normalizeSectionName(sectionName);
            return normalized.startsWith('ADUQualities');
        },
        isADUConditionsSection(sectionName: string): boolean {
            const normalized = this.normalizeSectionName(sectionName);
            return normalized.startsWith('ADUConditions');
        },
        isFieldNameInSectionCollection(collectionName: string, fieldName: string): boolean {
            if (!fieldName) {
                return false;
            }

            switch (collectionName) {
                case 'ExteriorQualities':
                    return fieldName.startsWith('EXT_QUAL_');
                case 'ExteriorConditions':
                    return fieldName.startsWith('EXT_COND_') || fieldName.startsWith('PU_EXT_COND_');
                case 'InteriorQualities':
                    return fieldName.startsWith('INT_QUAL_');
                case 'InteriorConditions':
                    return fieldName.startsWith('INT_COND_');
                case 'ADUQualities':
                    return fieldName === 'ADU_LOCATION' || fieldName.startsWith('ADU_QUAL_') || fieldName === 'INT_ADU_COND_BATHS';
                case 'ADUConditions':
                    return fieldName.startsWith('ADU_COND_');
                default:
                    return false;
            }
        },
        isRepeatableBlankLabelField(fieldName: string): boolean {
            const baseFieldName = String(fieldName || '').split('-').pop() || '';
            return /^(EXT_QUAL|EXT_COND|INT_QUAL|INT_COND|ADU_QUAL|ADU_COND)_BLANK_LABEL$/.test(baseFieldName);
        },
        getOwnersPayloadFieldName(field: any, OwnersIndices: number[]): string {
            const fieldName = String(field?.fieldName || '');
            if (!fieldName) {
                return fieldName;
            }

            if (/^Owners-\d+-/.test(fieldName)) {
                return fieldName;
            }

            if (!this.isOwnersField(fieldName)) {
                return fieldName;
            }

            if (!OwnersIndices.includes(0)) {
                return fieldName;
            }

            return `Owners-0-${fieldName}`;
        },
        getSectionCollectionPayloadFieldName(
            field: any,
            collectionName: string,
            collectionIndices: number[],
            isSectionField: (sectionName: string) => boolean
        ): string {
            const fieldName = String(field?.fieldName || '');
            if (!fieldName) {
                return fieldName;
            }

            if (new RegExp(`^${collectionName}-\\d+-`).test(fieldName)) {
                return fieldName;
            }

            const sectionName = String(field?.sectionName || '');
            if (!isSectionField(sectionName) && !this.isFieldNameInSectionCollection(collectionName, fieldName)) {
                return fieldName;
            }

            return collectionIndices.includes(0) ? `${collectionName}-0-${fieldName}` : fieldName;
        },
        getTopLevelCollectionPayloadFieldName(
            field: any,
            collectionName: string,
            collectionIndices: number[],
            isCollectionField: (fieldName: string) => boolean
        ): string {
            const fieldName = String(field?.fieldName || '');
            if (!fieldName) {
                return fieldName;
            }

            if (new RegExp(`^${collectionName}-\\d+-`).test(fieldName)) {
                return fieldName;
            }

            if (!isCollectionField(fieldName)) {
                return fieldName;
            }

            return collectionIndices.includes(0) ? `${collectionName}-0-${fieldName}` : fieldName;
        },
        getDwellingPayloadFieldName(
            field: any,
            dwellingIndices: number[],
            unitIndicesByDwelling: Record<number, number[]>,
            unitLevelIndicesByDwellingUnit: Record<number, Record<number, number[]>>,
            unitRoomIndicesByDwellingUnit: Record<number, Record<number, number[]>>,
            dwellingHeatingIndicesByDwelling: Record<number, number[]>,
            dwellingCoolingIndicesByDwelling: Record<number, number[]>,
            dwellingFoundationIndicesByDwelling: Record<number, number[]>,
            dwellingFinishedAreaRoomSummaryIndicesByDwelling: Record<number, number[]>,
            dwellingDefectIndicesByDwelling: Record<number, number[]>
        ): string {
            const fieldName = String(field?.fieldName || '');
            if (!fieldName) {
                return fieldName;
            }

            // Field is already in the canonical Dwellings-N-* format. Roof is a
            // nested object, so normalize its form fields into Dwellings-N-Roof-*.
            const canonicalDwellingMatch = fieldName.match(/^Dwellings-(\d+)-(.+)$/);
            if (canonicalDwellingMatch) {
                const dwellingIndex = canonicalDwellingMatch[1];
                const dwellingFieldName = canonicalDwellingMatch[2];
                const roofFieldNames = new Set([
                    'EXTRFSURFACE',
                    'EXTRFSURFACE_TYPE',
                    'EXTRFSURFACE_OBSERVABLE',
                    'EXTRFSURFACE_AGE',
                    'EXT_ROOF_STATUS',
                ]);
                return roofFieldNames.has(dwellingFieldName)
                    ? `Dwellings-${dwellingIndex}-Roof-${dwellingFieldName}`
                    : fieldName;
            }

            const sectionName = String(field?.sectionName || '').toLowerCase();
            const isDwellingSection = sectionName.includes('dwell');
            const isUnitSection = sectionName.includes('unit');
            const isUnitLevelField = fieldName.startsWith('LEVEL_');
            const isUnitRoomField = fieldName.startsWith('ROOM_');
            const isDwellingHeatingField =
                fieldName === 'DWELL_HEAT' || fieldName === 'HEAT_SYSTEM' || fieldName === 'HEAT_SYSTEM_FLATTENED' ||
                fieldName.endsWith('-DWELL_HEAT') || fieldName.endsWith('-HEAT_SYSTEM') ||
                fieldName.endsWith('-HEAT_SYSTEM_FLATTENED');
            const isDwellingCoolingField =
                fieldName === 'DWELL_COOL' || fieldName === 'COOL_SYSTEM' ||
                fieldName.endsWith('-DWELL_COOL') || fieldName.endsWith('-COOL_SYSTEM');
            const isDwellingFoundationField = fieldName.includes('FOUNDATION');
            const isDwellingFinishedAreaRoomSummaryField =
                fieldName === 'FINISHED_AREA_TOTAL_AREA' ||
                fieldName === 'FINISHED_AREA_TOTAL_AREA_FLATTENED';
            const isDwellingDefectField =
                fieldName === 'DEFECT_FEATURE' ||
                fieldName === 'DEFECT_FEATURE_FLATTENED';
            const isDwellingRoofField =
                fieldName === 'EXTRFSURFACE' ||
                fieldName === 'EXTRFSURFACE_TYPE' ||
                fieldName === 'EXTRFSURFACE_OBSERVABLE' ||
                fieldName === 'EXTRFSURFACE_AGE' ||
                fieldName === 'EXT_ROOF_STATUS';

            if (isUnitSection) {
                if (!dwellingIndices.includes(0)) {
                    return fieldName;
                }

                const unitIndicesForDwellingZero = unitIndicesByDwelling[0] || [];
                const unitZero = unitIndicesForDwellingZero.includes(0) ? 0 : unitIndicesForDwellingZero[0];
                if (unitZero === undefined) {
                    return fieldName;
                }

                if (isUnitLevelField) {
                    const levelIndicesForUnitZero = unitLevelIndicesByDwellingUnit[0]?.[unitZero] || [];
                    const levelZero = levelIndicesForUnitZero.includes(0) ? 0 : levelIndicesForUnitZero[0];
                    if (levelZero !== undefined) {
                        return `Dwellings-0-Units-${unitZero}-Levels-${levelZero}-${fieldName}`;
                    }
                }

                if (isUnitRoomField) {
                    const roomIndicesForUnitZero = unitRoomIndicesByDwellingUnit[0]?.[unitZero] || [];
                    const roomZero = roomIndicesForUnitZero.includes(0) ? 0 : roomIndicesForUnitZero[0];
                    if (roomZero !== undefined) {
                        return `Dwellings-0-Units-${unitZero}-Rooms-${roomZero}-${fieldName}`;
                    }
                }

                return `Dwellings-0-Units-${unitZero}-${fieldName}`;
            }

            if (isDwellingSection) {
                if (isDwellingRoofField && dwellingIndices.includes(0)) {
                    return `Dwellings-0-Roof-${fieldName}`;
                }

                if (isDwellingHeatingField && dwellingIndices.includes(0)) {
                    const heatingIndicesForDwellingZero = dwellingHeatingIndicesByDwelling[0] || [];
                    const heatingZero = heatingIndicesForDwellingZero.includes(0) ? 0 : heatingIndicesForDwellingZero[0];
                    if (heatingZero !== undefined) {
                        return `Dwellings-0-Heating-${heatingZero}-${fieldName}`;
                    }
                }

                if (isDwellingCoolingField && dwellingIndices.includes(0)) {
                    const coolingIndicesForDwellingZero = dwellingCoolingIndicesByDwelling[0] || [];
                    const coolingZero = coolingIndicesForDwellingZero.includes(0) ? 0 : coolingIndicesForDwellingZero[0];
                    if (coolingZero !== undefined) {
                        return `Dwellings-0-Cooling-${coolingZero}-${fieldName}`;
                    }
                }

                if (isDwellingFoundationField && dwellingIndices.includes(0)) {
                    const foundationIndicesForDwellingZero = dwellingFoundationIndicesByDwelling[0] || [];
                    const foundationZero = foundationIndicesForDwellingZero.includes(0) ? 0 : foundationIndicesForDwellingZero[0];
                    if (foundationZero !== undefined) {
                        return `Dwellings-0-Foundations-${foundationZero}-${fieldName}`;
                    }
                }

                if (isDwellingFinishedAreaRoomSummaryField && dwellingIndices.includes(0)) {
                    const indicesForDwellingZero = dwellingFinishedAreaRoomSummaryIndicesByDwelling[0] || [];
                    const collectionZero = indicesForDwellingZero.includes(0) ? 0 : indicesForDwellingZero[0];
                    if (collectionZero !== undefined) {
                        return `Dwellings-0-FinishedAreaRoomSummary-${collectionZero}-${fieldName}`;
                    }
                }

                if (isDwellingDefectField && dwellingIndices.includes(0)) {
                    const indicesForDwellingZero = dwellingDefectIndicesByDwelling[0] || [];
                    const collectionZero = indicesForDwellingZero.includes(0) ? 0 : indicesForDwellingZero[0];
                    if (collectionZero !== undefined) {
                        return `Dwellings-0-Defects-${collectionZero}-${fieldName}`;
                    }
                }

                return dwellingIndices.includes(0) ? `Dwellings-0-${fieldName}` : fieldName;
            }

            return fieldName;
        },
        getOutbuildingPayloadFieldName(field: any, outbuildingIndices: number[]): string {
            const fieldName = String(field?.fieldName || '');
            if (!fieldName) {
                return fieldName;
            }

            const sectionName = String(field?.sectionName || '').toLowerCase();
            const isOutbuildingSection = sectionName === 'outbuilding' || sectionName.startsWith('outbuilding#') || fieldName.includes('OUTBUILDING') || fieldName.startsWith('MECH_HEAT');
            if (!isOutbuildingSection) {
                return fieldName;
            }

            // Field is already in the canonical Outbuildings-N-FIELDNAME format.
            if (/^Outbuildings-\d+-/.test(fieldName)) {
                return fieldName;
            }

            return outbuildingIndices.includes(0) ? this.getOutbuildingFieldNameForIndex(fieldName, 0) : fieldName;
        },
        getOutbuildingFieldNameForIndex(fieldName: string, outbuildingIndex: number): string {
            if (fieldName === 'OUTBUILDING_UTILITIES') {
                return `Outbuildings-${outbuildingIndex}-OutbuildingUtilities-0-${fieldName}`;
            }

            if (fieldName.startsWith('MECH_HEAT')) {
                return `Outbuildings-${outbuildingIndex}-MechanicalHeatSystem-0-${fieldName}`;
            }

            if (fieldName === 'BL_OUTBUILDING' || fieldName === 'BL_OUTBUILDING_BLANK') {
                return `Outbuildings-${outbuildingIndex}-BLOutbuilding-0-${fieldName}`;
            }

            return `Outbuildings-${outbuildingIndex}-${fieldName}`;
        },
        getVehicleStoragePayloadFieldName(field: any, vehicleStorageIndices: number[]): string {
            const fieldName = String(field?.fieldName || '');
            if (!fieldName) {
                return fieldName;
            }

            const sectionName = String(field?.sectionName || '').toLowerCase();
            const normalizedSectionName = sectionName.replace(/\s+/g, '');
            const isVehicleStorageSection =
                normalizedSectionName === 'vehiclestorage' ||
                normalizedSectionName.startsWith('vehiclestorage#') ||
                fieldName.includes('VEHICLE_STORAGE') ||
                fieldName.includes('VS_');
            if (!isVehicleStorageSection) {
                return fieldName;
            }

            // Field is already in the canonical VehicleStorage-N-FIELDNAME format.
            if (/^VehicleStorage-\d+-/.test(fieldName)) {
                return fieldName;
            }

            return vehicleStorageIndices.includes(0) ? `VehicleStorage-0-${fieldName}` : fieldName;
        },
        getAmenityPayloadFieldName(field: any, amenityCollectionIndices: Record<string, number[]>): string {
            const fieldName = String(field?.fieldName || '');
            if (!fieldName) {
                return fieldName;
            }

            // Already in canonical Amenities-N-... format.
            if (/^Amenities-\d+-/.test(fieldName)) {
                return fieldName;
            }

            const sectionName = String(field?.sectionName || '').toLowerCase();
            const normalizedSectionName = sectionName.replace(/\s+/g, '');
            const isAmenitySection =
                normalizedSectionName === 'propertyamenities' ||
                normalizedSectionName.startsWith('propertyamenities#') ||
                fieldName.includes('AMENITY') ||
                fieldName.includes('SPA_AMENITY');
            if (!isAmenitySection) {
                return fieldName;
            }

            const collectionName = this.getAmenityCollectionNameForField(fieldName);
            if (collectionName) {
                const collectionIndices = amenityCollectionIndices[collectionName] || [];
                const collectionZero = collectionIndices.includes(0) ? 0 : (collectionIndices[0] ?? 0);
                return `Amenities-0-${collectionName}-${collectionZero}-${fieldName}`;
            }

            // Fields not belonging to a named sub-collection (e.g. SPA_AMENITY_* from form v1).
            return `Amenities-0-${fieldName}`;
        },
        expandAmenityFieldsByPayload(formFields: any[], amenityCollectionIndices: Record<string, number[]>): any[] {
            const hasRepeating = Object.values(amenityCollectionIndices).some((indices) => indices.length > 1);
            if (!Array.isArray(formFields) || !hasRepeating) {
                return formFields;
            }

            const isAmenityBaseField = (field: any): boolean => {
                const fieldName = String(field?.fieldName || '');
                const sectionName = String(field?.sectionName || '').toLowerCase().replace(/\s+/g, '');
                if (fieldName.startsWith('Amenities-')) {
                    return false;
                }
                return sectionName === 'propertyamenities' || fieldName.includes('AMENITY') || fieldName.includes('SPA_AMENITY');
            };

            const baseFields = formFields.filter((field: any) => isAmenityBaseField(field));
            if (baseFields.length === 0) {
                return formFields;
            }

            const cleanedFields = formFields.filter((field: any) => !String(field?.fieldName || '').startsWith('Amenities-'));

            // Insert each field's repeat clones immediately after that field's own base row
            // so alike rows are grouped together.
            let result = [...cleanedFields];

            for (const baseField of baseFields) {
                const baseFieldName = String(baseField?.fieldName || '');
                const collectionName = this.getAmenityCollectionNameForField(baseFieldName);
                if (!collectionName) {
                    continue;
                }

                const collectionIndices = amenityCollectionIndices[collectionName] || [];
                if (collectionIndices.length <= 1) {
                    continue;
                }

                const baseIdx = result.findIndex((f: any) => f === baseField);
                if (baseIdx < 0) {
                    continue;
                }

                const clones: any[] = [];
                // Index 0 is represented by the base template row; clone indices 1+.
                for (let i = 1; i < collectionIndices.length; i++) {
                    const collectionIndex = collectionIndices[i];
                    const clone = _.cloneDeep(baseField);
                    clone.fieldName = `Amenities-0-${collectionName}-${collectionIndex}-${baseFieldName}`;
                    // Keep a single section header bar (like Heating/Cooling within Dwelling).
                    clone.sectionName = baseField.sectionName;
                    clones.push(clone);
                }

                if (clones.length > 0) {
                    result.splice(baseIdx + 1, 0, ...clones);
                }
            }

            return result;
        },
        expandOutbuildingCollectionFieldsByPayload(
            formFields: any[],
            outbuildingIndices: number[],
            collectionIndicesByOutbuilding: Record<number, number[]>,
            collectionName: string,
            collectionFieldNames: string[]
        ): any[] {
            if (!Array.isArray(formFields) || Object.keys(collectionIndicesByOutbuilding).length === 0) {
                return formFields;
            }

            const hasRepeating = Object.values(collectionIndicesByOutbuilding).some((indices) => indices.length > 1);
            if (!hasRepeating) {
                return formFields;
            }

            let result = [...formFields];

            for (const outbuildingIndex of outbuildingIndices) {
                const collectionIndices = collectionIndicesByOutbuilding[outbuildingIndex] || [];
                if (collectionIndices.length <= 1) continue;

                // For outbuilding 0 the form field is still the raw base name (resolved at grid time).
                // For outbuilding N>0 it was already prefixed by expandOutbuildingFieldsByPayload.
                const isBaseCollectionField = (field: any): boolean => {
                    const fn = String(field?.fieldName || '');
                    if (outbuildingIndex === 0) {
                        return collectionFieldNames.includes(fn);
                    }
                    return collectionFieldNames.some((fieldName) =>
                        fn === `Outbuildings-${outbuildingIndex}-${collectionName}-0-${fieldName}`
                    );
                };

                const baseIdx = result.findIndex((f: any) => isBaseCollectionField(f));
                if (baseIdx < 0) continue;

                const baseField = result[baseIdx];
                const clones: any[] = [];

                // Index 0 is represented by the base template row; clone indices 1+.
                for (let i = 1; i < collectionIndices.length; i++) {
                    const collectionIndex = collectionIndices[i];
                    const clone = _.cloneDeep(baseField);
                    const baseFieldName = String(baseField.fieldName || '');
                    const fieldName = outbuildingIndex === 0
                        ? baseFieldName
                        : baseFieldName.replace(`Outbuildings-${outbuildingIndex}-${collectionName}-0-`, '');
                    clone.fieldName = `Outbuildings-${outbuildingIndex}-${collectionName}-${collectionIndex}-${fieldName}`;
                    clone.sectionName = baseField.sectionName;
                    clones.push(clone);
                }

                if (clones.length > 0) {
                    result.splice(baseIdx + 1, 0, ...clones);
                }
            }

            return result;
        },
        expandOutbuildingFieldsByPayload(formFields: any[], outbuildingIndices: number[]): any[] {
            if (!Array.isArray(formFields) || outbuildingIndices.length <= 1) {
                return formFields;
            }

            const isOutbuildingBaseField = (field: any): boolean => {
                const fieldName = String(field?.fieldName || '');
                const sectionName = String(field?.sectionName || '');
                if (fieldName.startsWith('Outbuildings-')) {
                    return false;
                }
                return sectionName.toLowerCase() === 'outbuilding' || fieldName.includes('OUTBUILDING') || fieldName.startsWith('MECH_HEAT');
            };

            const baseFields = formFields.filter((field: any) => isOutbuildingBaseField(field));
            if (baseFields.length === 0) {
                return formFields;
            }

            const cleanedFields = formFields.filter((field: any) => !String(field?.fieldName || '').startsWith('Outbuildings-'));
            const insertionIndex = cleanedFields.lastIndexOf(baseFields[baseFields.length - 1]);
            if (insertionIndex < 0) {
                return formFields;
            }

            const clonedFields: any[] = [];
            for (let i = 1; i < outbuildingIndices.length; i++) {
                const variant = outbuildingIndices[i];
                const variantSectionName = `Outbuilding#${variant}`;
                for (const baseField of baseFields) {
                    const clone = _.cloneDeep(baseField);
                    clone.fieldName = this.getOutbuildingFieldNameForIndex(String(baseField.fieldName || ''), variant);
                    clone.sectionName = variantSectionName;
                    clonedFields.push(clone);
                }
            }

            return [
                ...cleanedFields.slice(0, insertionIndex + 1),
                ...clonedFields,
                ...cleanedFields.slice(insertionIndex + 1),
            ];
        },
        expandVehicleStorageFieldsByPayload(formFields: any[], vehicleStorageIndices: number[]): any[] {
            if (!Array.isArray(formFields) || vehicleStorageIndices.length <= 1) {
                return formFields;
            }

            const isVehicleStorageBaseField = (field: any): boolean => {
                const fieldName = String(field?.fieldName || '');
                const sectionName = String(field?.sectionName || '');
                const normalizedSectionName = sectionName.toLowerCase().replace(/\s+/g, '');
                if (fieldName.startsWith('VehicleStorage-')) {
                    return false;
                }
                // Keep Vehicle Storage - Other as a single non-repeating row.
                if (fieldName === 'BL_VEHICLE_STORAGE' || fieldName.endsWith('-BL_VEHICLE_STORAGE')) {
                    return false;
                }
                return normalizedSectionName === 'vehiclestorage' || fieldName.includes('VEHICLE_STORAGE') || fieldName.includes('VS_');
            };

            const baseFields = formFields.filter((field: any) => isVehicleStorageBaseField(field));
            if (baseFields.length === 0) {
                return formFields;
            }

            const cleanedFields = formFields.filter((field: any) => !String(field?.fieldName || '').startsWith('VehicleStorage-'));
            const insertionIndex = cleanedFields.lastIndexOf(baseFields[baseFields.length - 1]);
            if (insertionIndex < 0) {
                return formFields;
            }

            const clonedFields: any[] = [];
            for (let i = 1; i < vehicleStorageIndices.length; i++) {
                const variant = vehicleStorageIndices[i];
                for (const baseField of baseFields) {
                    const clone = _.cloneDeep(baseField);
                    clone.fieldName = `VehicleStorage-${variant}-${baseField.fieldName}`;

                    // Keep a single section header bar for Vehicle Storage.
                    clone.sectionName = baseField.sectionName;
                    clonedFields.push(clone);
                }
            }

            return [
                ...cleanedFields.slice(0, insertionIndex + 1),
                ...clonedFields,
                ...cleanedFields.slice(insertionIndex + 1),
            ];
        },
        expandTopLevelCollectionFieldsByPayload(
            formFields: any[],
            collectionName: string,
            collectionIndices: number[],
            isCollectionBaseField: (fieldName: string) => boolean
        ): any[] {
            if (!Array.isArray(formFields) || collectionIndices.length <= 1) {
                return formFields;
            }

            const baseFields = formFields.filter((field: any) => {
                const fieldName = String(field?.fieldName || '');
                if (!fieldName || fieldName.startsWith(`${collectionName}-`)) {
                    return false;
                }

                return isCollectionBaseField(fieldName);
            });

            if (baseFields.length === 0) {
                return formFields;
            }

            const result = [...formFields];
            for (const baseField of baseFields) {
                const baseFieldName = String(baseField?.fieldName || '');
                const baseFieldIndex = result.findIndex((field: any) => field === baseField);
                if (baseFieldIndex < 0) {
                    continue;
                }

                const clones: any[] = [];
                for (let i = 1; i < collectionIndices.length; i++) {
                    const collectionIndex = collectionIndices[i];
                    const clone = _.cloneDeep(baseField);
                    clone.fieldName = `${collectionName}-${collectionIndex}-${baseFieldName}`;
                    clone.sectionName = baseField.sectionName;
                    clones.push(clone);
                }

                if (clones.length > 0) {
                    result.splice(baseFieldIndex + 1, 0, ...clones);
                }
            }

            return result;
        },
        expandSaleListingInformationFieldsByPayload(formFields: any[], collectionIndices: number[]): any[] {
            if (!Array.isArray(formFields) || collectionIndices.length <= 1) {
                return formFields;
            }

            const orderedBaseFieldNames = ['SLI_LIST_STATUS', 'SLI_LIST_CUR_OR_FINAL_LP'];
            const baseFields = orderedBaseFieldNames
                .map((fieldName) => formFields.find((field: any) => field?.fieldName === fieldName))
                .filter((field: any) => !!field);
            if (baseFields.length === 0) {
                return formFields;
            }

            const firstBaseFieldIndex = Math.min(...baseFields.map((field: any) => formFields.indexOf(field)));
            const fieldsBeforeInsertion = formFields
                .slice(0, firstBaseFieldIndex)
                .filter((field: any) => !String(field?.fieldName || '').startsWith('SaleListingInformation-'));
            const remainingFields = formFields
                .slice(firstBaseFieldIndex)
                .filter((field: any) => {
                    const fieldName = String(field?.fieldName || '');
                    return !orderedBaseFieldNames.includes(fieldName) &&
                        !fieldName.startsWith('SaleListingInformation-');
                });
            const orderedFields: any[] = [];

            for (const collectionIndex of collectionIndices) {
                for (const baseField of baseFields) {
                    if (collectionIndex === 0) {
                        orderedFields.push(baseField);
                        continue;
                    }

                    const clone = _.cloneDeep(baseField);
                    clone.fieldName = `SaleListingInformation-${collectionIndex}-${baseField.fieldName}`;
                    clone.sectionName = baseField.sectionName;
                    orderedFields.push(clone);
                }
            }

            return [...fieldsBeforeInsertion, ...orderedFields, ...remainingFields];
        },
        expandOwnersFieldsByPayload(formFields: any[], OwnersIndices: number[]): any[] {
            if (!Array.isArray(formFields) || OwnersIndices.length <= 1) {
                return formFields;
            }

            const baseFields = formFields.filter((field: any) => {
                const fieldName = String(field?.fieldName || '');
                if (!fieldName || fieldName.startsWith('Owners-')) {
                    return false;
                }

                return this.isOwnersField(fieldName);
            });

            if (baseFields.length === 0) {
                return formFields;
            }

            const cleanedFields = formFields.filter((field: any) => !String(field?.fieldName || '').startsWith('Owners-'));
            const insertionIndex = cleanedFields.lastIndexOf(baseFields[baseFields.length - 1]);
            if (insertionIndex < 0) {
                return formFields;
            }

            const clonedFields: any[] = [];
            for (let i = 1; i < OwnersIndices.length; i++) {
                const ownerIndex = OwnersIndices[i];
                for (const baseField of baseFields) {
                    const clone = _.cloneDeep(baseField);
                    clone.fieldName = `Owners-${ownerIndex}-${baseField.fieldName}`;

                    // Keep section styling aligned with index 0 row block.
                    clone.sectionName = baseField.sectionName;
                    clonedFields.push(clone);
                }
            }

            return [
                ...cleanedFields.slice(0, insertionIndex + 1),
                ...clonedFields,
                ...cleanedFields.slice(insertionIndex + 1),
            ];
        },
        expandAssessorParcelFieldsByPayload(formFields: any[], assessorParcelIndices: number[]): any[] {
            if (!Array.isArray(formFields) || assessorParcelIndices.length <= 1) {
                return formFields;
            }

            const baseFields = formFields.filter((field: any) => {
                const fieldName = String(field?.fieldName || '');
                return fieldName && !fieldName.startsWith('AssessorParcels-') && this.isAssessorParcelField(fieldName);
            });

            if (baseFields.length === 0) {
                return formFields;
            }

            const cleanedFields = formFields.filter((field: any) => !String(field?.fieldName || '').startsWith('AssessorParcels-'));
            const insertionIndex = cleanedFields.lastIndexOf(baseFields[baseFields.length - 1]);
            if (insertionIndex < 0) {
                return formFields;
            }

            const clonedFields: any[] = [];
            for (let i = 1; i < assessorParcelIndices.length; i++) {
                const parcelIndex = assessorParcelIndices[i];
                for (const baseField of baseFields) {
                    const clone = _.cloneDeep(baseField);
                    clone.fieldName = `AssessorParcels-${parcelIndex}-${baseField.fieldName}`;
                    clone.sectionName = baseField.sectionName;
                    clonedFields.push(clone);
                }
            }

            return [
                ...cleanedFields.slice(0, insertionIndex + 1),
                ...clonedFields,
                ...cleanedFields.slice(insertionIndex + 1),
            ];
        },
        expandTransferHistoryFieldsByPayload(formFields: any[], transferHistoryIndices: number[]): any[] {
            if (!Array.isArray(formFields) || transferHistoryIndices.length <= 1) {
                return formFields;
            }

            const baseFields = formFields.filter((field: any) => {
                const fieldName = String(field?.fieldName || '');
                if (!fieldName || fieldName.startsWith('TransferHistories-')) {
                    return false;
                }

                return this.isTransferHistoryField(fieldName);
            });

            if (baseFields.length === 0) {
                return formFields;
            }

            const cleanedFields = formFields.filter((field: any) => !String(field?.fieldName || '').startsWith('TransferHistories-'));
            const insertionIndex = cleanedFields.lastIndexOf(baseFields[baseFields.length - 1]);
            if (insertionIndex < 0) {
                return formFields;
            }

            const clonedFields: any[] = [];
            for (let i = 1; i < transferHistoryIndices.length; i++) {
                const transferHistoryIndex = transferHistoryIndices[i];
                for (const baseField of baseFields) {
                    const clone = _.cloneDeep(baseField);
                    clone.fieldName = `TransferHistories-${transferHistoryIndex}-${baseField.fieldName}`;
                    clone.sectionName = baseField.sectionName;
                    clonedFields.push(clone);
                }
            }

            return [
                ...cleanedFields.slice(0, insertionIndex + 1),
                ...clonedFields,
                ...cleanedFields.slice(insertionIndex + 1),
            ];
        },
        expandSectionCollectionFieldsByPayload(
            formFields: any[],
            collectionName: string,
            collectionIndices: number[],
            isSectionField: (sectionName: string) => boolean
        ): any[] {
            if (!Array.isArray(formFields) || collectionIndices.length <= 1) {
                return formFields;
            }

            const baseFields = formFields.filter((field: any) => {
                const fieldName = String(field?.fieldName || '');
                const sectionName = String(field?.sectionName || '');
                if (!fieldName || fieldName.startsWith(`${collectionName}-`)) {
                    return false;
                }

                return isSectionField(sectionName) || this.isFieldNameInSectionCollection(collectionName, fieldName);
            });

            if (baseFields.length === 0) {
                return formFields;
            }

            const cleanedFields = formFields.filter((field: any) => !String(field?.fieldName || '').startsWith(`${collectionName}-`));
            const insertionIndex = cleanedFields.lastIndexOf(baseFields[baseFields.length - 1]);
            if (insertionIndex < 0) {
                return formFields;
            }

            const clonedFields: any[] = [];
            for (let i = 1; i < collectionIndices.length; i++) {
                const collectionIndex = collectionIndices[i];
                for (const baseField of baseFields) {
                    const clone = _.cloneDeep(baseField);
                    clone.fieldName = `${collectionName}-${collectionIndex}-${baseField.fieldName}`;
                    clone.sectionName = baseField.sectionName;
                    clonedFields.push(clone);
                }
            }

            return [
                ...cleanedFields.slice(0, insertionIndex + 1),
                ...clonedFields,
                ...cleanedFields.slice(insertionIndex + 1),
            ];
        },
        getFirstSourceValueByFlattenedKey(flattenedKey: string): string {
            if (!flattenedKey) {
                return '';
            }

            for (const setting of this.sourceColumnOrder || []) {
                const initials = setting?.initials;
                if (!initials) {
                    continue;
                }

                const sourceData = CompUtils.getDataSourceSourceData(this.activeComp, initials);
                if (!sourceData || Array.isArray(sourceData) || typeof sourceData !== 'object') {
                    continue;
                }

                if (!Object.prototype.hasOwnProperty.call(sourceData, flattenedKey)) {
                    continue;
                }

                const rawValue = sourceData[flattenedKey];
                if (rawValue === null || rawValue === undefined) {
                    continue;
                }

                const value = String(rawValue).trim();
                if (value !== '') {
                    return value;
                }
            }

            // Index discovery includes Transfer to Form rows, so value lookup must include
            // them as a fallback as well. Otherwise transfer-only Levels and Rooms are
            // discovered but cannot be matched and all of their Room rows are appended last.
            if (Array.isArray(this.activeComp?.sourceData)) {
                const transferData = this.activeComp.sourceData.find(
                    (item: any) => String(item?.fieldName || '') === flattenedKey
                );
                const transferValue = String(transferData?.value ?? '').trim();
                if (transferValue !== '') {
                    return transferValue;
                }
            }

            return '';
        },
        normalizeLevelRoomGroupingValue(value: string): string {
            // Atomic fields normally contain only "Level 1". Transfer-only imports can
            // contain their flattened display value, so retain only the atomic first part.
            return String(value || '').split('|')[0].trim().toLowerCase();
        },
        expandDwellingsAndUnitsByPayload(
            formFields: any[],
            dwellingIndices: number[],
            unitIndicesByDwelling: Record<number, number[]>,
            unitLevelIndicesByDwellingUnit: Record<number, Record<number, number[]>>,
            unitRoomIndicesByDwellingUnit: Record<number, Record<number, number[]>>,
            dwellingHeatingIndicesByDwelling: Record<number, number[]>,
            dwellingCoolingIndicesByDwelling: Record<number, number[]>,
            dwellingFoundationIndicesByDwelling: Record<number, number[]>,
            dwellingFinishedAreaRoomSummaryIndicesByDwelling: Record<number, number[]>,
            dwellingDefectIndicesByDwelling: Record<number, number[]>
        ): any[] {
            if (!Array.isArray(formFields) || dwellingIndices.length === 0) {
                return formFields;
            }

            const hasMultipleDwellings = dwellingIndices.length > 1;
            const hasRepeatingUnits = Object.values(unitIndicesByDwelling).some((indices) => indices.length > 1);
            const hasRepeatingUnitLevels = Object.values(unitLevelIndicesByDwellingUnit).some((unitMap) =>
                Object.values(unitMap).some((indices) => indices.length > 0)
            );
            const hasRepeatingUnitRooms = Object.values(unitRoomIndicesByDwellingUnit).some((unitMap) =>
                Object.values(unitMap).some((indices) => indices.length > 0)
            );
            const hasRepeatingDwellingHeating = Object.values(dwellingHeatingIndicesByDwelling).some((indices) => indices.length > 1);
            const hasRepeatingDwellingCooling = Object.values(dwellingCoolingIndicesByDwelling).some((indices) => indices.length > 1);
            const hasRepeatingDwellingFoundations = Object.values(dwellingFoundationIndicesByDwelling).some((indices) => indices.length > 1);
            const hasRepeatingDwellingFinishedAreaRoomSummaries = Object.values(dwellingFinishedAreaRoomSummaryIndicesByDwelling).some((indices) => indices.length > 1);
            const hasRepeatingDwellingDefects = Object.values(dwellingDefectIndicesByDwelling).some((indices) => indices.length > 1);
            if (
                !hasMultipleDwellings &&
                !hasRepeatingUnits &&
                !hasRepeatingUnitLevels &&
                !hasRepeatingUnitRooms &&
                !hasRepeatingDwellingHeating &&
                !hasRepeatingDwellingCooling &&
                !hasRepeatingDwellingFoundations &&
                !hasRepeatingDwellingFinishedAreaRoomSummaries &&
                !hasRepeatingDwellingDefects
            ) {
                return formFields;
            }

            const isDwellingOrUnitSection = (field: any): boolean => {
                const sectionName = String(field?.sectionName || '').toLowerCase();
                const fieldName = String(field?.fieldName || '');
                return sectionName.includes('dwell') ||
                    sectionName.includes('unit') ||
                    fieldName === 'LEVEL_IN_UNIT' ||
                    fieldName === 'ROOM_LEVEL';
            };

            const cleanedFields = formFields.filter((field: any) => {
                const fieldName = String(field?.fieldName || '');
                return !fieldName.startsWith('Dwellings-') && fieldName !== 'ROOM_ROOM_TYPE';
            });

            const firstDwellingOrUnitIdx = cleanedFields.findIndex((field: any) => isDwellingOrUnitSection(field));
            if (firstDwellingOrUnitIdx === -1) {
                return formFields;
            }

            let lastDwellingOrUnitIdx = -1;
            for (let i = cleanedFields.length - 1; i >= 0; i--) {
                if (isDwellingOrUnitSection(cleanedFields[i])) {
                    lastDwellingOrUnitIdx = i;
                    break;
                }
            }

            if (lastDwellingOrUnitIdx < firstDwellingOrUnitIdx) {
                return formFields;
            }

            const templateFields = cleanedFields.slice(firstDwellingOrUnitIdx, lastDwellingOrUnitIdx + 1);
            const dwellingTemplateFields = templateFields.filter((field: any) => String(field?.sectionName || '').toLowerCase().includes('dwell'));
            const unitTemplateFields = templateFields.filter((field: any) => String(field?.sectionName || '').toLowerCase().includes('unit'));
            const unitTemplateLevelFields = templateFields.filter(
                (field: any) => String(field?.fieldName || '') === 'LEVEL_IN_UNIT'
            );
            const roomTemplateField = formFields.find(
                (field: any) => String(field?.fieldName || '') === 'ROOM_LEVEL'
            ) || formFields.find(
                (field: any) => String(field?.fieldName || '') === 'ROOM_ROOM_TYPE'
            ) || unitTemplateLevelFields[0];
            const unitTemplateRoomFields = roomTemplateField
                ? [{ ..._.cloneDeep(roomTemplateField), fieldName: 'ROOM_LEVEL', friendlyFieldName: 'Room' }]
                : [];
            const nonDwellingOrUnitFields = templateFields.filter((field: any) => {
                const fieldName = String(field?.fieldName || '');
                return !isDwellingOrUnitSection(field) && fieldName !== 'LEVEL_IN_UNIT' && fieldName !== 'ROOM_LEVEL';
            });

            if (dwellingTemplateFields.length === 0) {
                return formFields;
            }

            const expandedFields: any[] = [];
            const sortedDwellingIndices = [...dwellingIndices].sort((a, b) => a - b);

            for (const dwellingIndex of sortedDwellingIndices) {
                for (const baseField of dwellingTemplateFields) {
                    const baseFieldName = String(baseField?.fieldName || '');
                    const isDwellingHeatingField = baseFieldName === 'DWELL_HEAT' || baseFieldName === 'HEAT_SYSTEM';
                    const isDwellingCoolingField = baseFieldName === 'DWELL_COOL' || baseFieldName === 'COOL_SYSTEM';
                    const isDwellingFoundationField = baseFieldName.includes('FOUNDATION');
                    const isDwellingFinishedAreaRoomSummaryField =
                        baseFieldName === 'FINISHED_AREA_TOTAL_AREA' ||
                        baseFieldName === 'FINISHED_AREA_TOTAL_AREA_FLATTENED';
                    const isDwellingDefectField =
                        baseFieldName === 'DEFECT_FEATURE' ||
                        baseFieldName === 'DEFECT_FEATURE_FLATTENED';

                    let collectionIndices = [0];
                    let collectionName = '';

                    if (isDwellingHeatingField) {
                        collectionName = 'Heating';
                        collectionIndices = dwellingHeatingIndicesByDwelling[dwellingIndex] || [0];
                    } else if (isDwellingCoolingField) {
                        collectionName = 'Cooling';
                        collectionIndices = dwellingCoolingIndicesByDwelling[dwellingIndex] || [0];
                    } else if (isDwellingFoundationField) {
                        collectionName = 'Foundations';
                        collectionIndices = dwellingFoundationIndicesByDwelling[dwellingIndex] || [0];
                    } else if (isDwellingFinishedAreaRoomSummaryField) {
                        collectionName = 'FinishedAreaRoomSummary';
                        collectionIndices = dwellingFinishedAreaRoomSummaryIndicesByDwelling[dwellingIndex] || [0];
                    } else if (isDwellingDefectField) {
                        collectionName = 'Defects';
                        collectionIndices = dwellingDefectIndicesByDwelling[dwellingIndex] || [0];
                    }

                    for (const collectionIndex of collectionIndices) {
                        const clone = _.cloneDeep(baseField);

                        if (collectionName) {
                            clone.fieldName = `Dwellings-${dwellingIndex}-${collectionName}-${collectionIndex}-${baseFieldName}`;
                        } else {
                            clone.fieldName = `Dwellings-${dwellingIndex}-${baseFieldName}`;
                        }

                        if (dwellingIndex === 0) {
                            clone.sectionName = baseField.sectionName;
                        } else {
                            clone.sectionName = `${baseField.sectionName}#${dwellingIndex}`;
                        }

                        expandedFields.push(clone);
                    }
                }

                const discoveredUnitIndices = unitIndicesByDwelling[dwellingIndex] || [];
                const effectiveUnitIndices = discoveredUnitIndices.length > 0 ? discoveredUnitIndices : (dwellingIndex === 0 ? [0] : []);

                const unitTemplateOtherFields = unitTemplateFields.filter((field: any) => {
                    const baseFieldName = String(field?.fieldName || '');
                    return !baseFieldName.startsWith('LEVEL_') && !baseFieldName.startsWith('ROOM_');
                });
                // LEVEL_IN_UNIT and ROOM_LEVEL are the grid row templates. Their displayed
                // source values resolve to *_FLATTENED, while the atomic payload values are
                // used below only to associate Rooms with their Level.
                for (const unitIndex of effectiveUnitIndices) {
                    const levelIndices = unitLevelIndicesByDwellingUnit[dwellingIndex]?.[unitIndex] || [0];
                    const roomIndices = unitRoomIndicesByDwellingUnit[dwellingIndex]?.[unitIndex] || [0];

                    const setUnitSectionName = (baseField: any, clone: any) => {
                        if (dwellingIndex === 0 && unitIndex === 0) {
                            clone.sectionName = baseField.sectionName;
                        } else {
                            clone.sectionName = `${baseField.sectionName}#${dwellingIndex}-${unitIndex}`;
                        }
                    };

                    for (const baseField of unitTemplateOtherFields) {
                        const clone = _.cloneDeep(baseField);
                        const baseFieldName = String(baseField?.fieldName || '');
                        clone.fieldName = `Dwellings-${dwellingIndex}-Units-${unitIndex}-${baseFieldName}`;
                        setUnitSectionName(baseField, clone);
                        expandedFields.push(clone);
                    }

                    const levelValueByIndex = new Map<number, string>();
                    for (const levelIndex of levelIndices) {
                        // Group by the atomic value. The LEVEL_IN_UNIT grid row still renders
                        // LEVEL_IN_UNIT_FLATTENED through getPreferredFieldNameCandidates().
                        const levelFieldName = `Dwellings-${dwellingIndex}-Units-${unitIndex}-Levels-${levelIndex}-LEVEL_IN_UNIT`;
                        levelValueByIndex.set(
                            levelIndex,
                            this.normalizeLevelRoomGroupingValue(this.getFirstSourceValueByFlattenedKey(levelFieldName))
                        );
                    }

                    const roomValueByIndex = new Map<number, string>();
                    for (const roomIndex of roomIndices) {
                        // Match the room's atomic ROOM_LEVEL to LEVEL_IN_UNIT. The ROOM_LEVEL
                        // grid row renders ROOM_LEVEL_FLATTENED rather than this atomic value.
                        const roomFieldName = `Dwellings-${dwellingIndex}-Units-${unitIndex}-Rooms-${roomIndex}-ROOM_LEVEL`;
                        roomValueByIndex.set(
                            roomIndex,
                            this.normalizeLevelRoomGroupingValue(this.getFirstSourceValueByFlattenedKey(roomFieldName))
                        );
                    }

                    const matchedRoomIndicesByLevel = new Map<number, number[]>();
                    const unmatchedRoomIndices: number[] = [];
                    for (const roomIndex of roomIndices) {
                        const roomValue = roomValueByIndex.get(roomIndex) || '';
                        const matchingLevelIndex = levelIndices.find((levelIndex) => {
                            const levelValue = levelValueByIndex.get(levelIndex) || '';
                            return roomValue !== '' && levelValue !== '' && roomValue === levelValue;
                        });

                        if (matchingLevelIndex === undefined) {
                            unmatchedRoomIndices.push(roomIndex);
                            continue;
                        }

                        if (!matchedRoomIndicesByLevel.has(matchingLevelIndex)) {
                            matchedRoomIndicesByLevel.set(matchingLevelIndex, []);
                        }

                        matchedRoomIndicesByLevel.get(matchingLevelIndex)!.push(roomIndex);
                    }

                    for (const levelIndex of levelIndices) {
                        for (const baseField of unitTemplateLevelFields) {
                            const clone = _.cloneDeep(baseField);
                            const baseFieldName = String(baseField?.fieldName || '');
                            clone.fieldName = `Dwellings-${dwellingIndex}-Units-${unitIndex}-Levels-${levelIndex}-${baseFieldName}`;
                            setUnitSectionName(baseField, clone);
                            expandedFields.push(clone);
                        }

                        const relatedRoomIndices = matchedRoomIndicesByLevel.get(levelIndex) || [];
                        for (const roomIndex of relatedRoomIndices) {
                            for (const baseField of unitTemplateRoomFields) {
                                const clone = _.cloneDeep(baseField);
                                const baseFieldName = String(baseField?.fieldName || '');
                                clone.fieldName = `Dwellings-${dwellingIndex}-Units-${unitIndex}-Rooms-${roomIndex}-${baseFieldName}`;
                                clone.friendlyFieldName = 'Room';
                                setUnitSectionName(baseField, clone);
                                expandedFields.push(clone);
                            }
                        }
                    }

                    for (const roomIndex of unmatchedRoomIndices) {
                        for (const baseField of unitTemplateRoomFields) {
                            const clone = _.cloneDeep(baseField);
                            const baseFieldName = String(baseField?.fieldName || '');
                            clone.fieldName = `Dwellings-${dwellingIndex}-Units-${unitIndex}-Rooms-${roomIndex}-${baseFieldName}`;
                            clone.friendlyFieldName = 'Room';
                            setUnitSectionName(baseField, clone);
                            expandedFields.push(clone);
                        }
                    }
                }
            }

            return [
                ...cleanedFields.slice(0, firstDwellingOrUnitIdx),
                ...expandedFields,
                ...nonDwellingOrUnitFields,
                ...cleanedFields.slice(lastDwellingOrUnitIdx + 1),
            ];
        },
        async applyTransferToFormForRepeatingSections(rowDatas: GridRowData[]): Promise<void> {
            if (!Array.isArray(rowDatas) || rowDatas.length === 0) {
                return;
            }

            const dwellingIndices = this.getDwellingIndicesFromPayload(this.activeComp);
            const unitIndicesByDwelling = this.getDwellingUnitIndicesByDwellingFromPayload(this.activeComp);
            const unitLevelIndicesByDwellingUnit = this.getUnitLevelIndicesByDwellingUnitFromPayload(this.activeComp);
            const unitRoomIndicesByDwellingUnit = this.getUnitRoomIndicesByDwellingUnitFromPayload(this.activeComp);
            const dwellingHeatingIndicesByDwelling = this.getDwellingHeatingIndicesByDwellingFromPayload(this.activeComp);
            const dwellingCoolingIndicesByDwelling = this.getDwellingCoolingIndicesByDwellingFromPayload(this.activeComp);
            const dwellingFoundationIndicesByDwelling = this.getDwellingFoundationIndicesByDwellingFromPayload(this.activeComp);
            const dwellingFinishedAreaRoomSummaryIndicesByDwelling = this.getDwellingFinishedAreaRoomSummaryIndicesByDwellingFromPayload(this.activeComp);
            const dwellingDefectIndicesByDwelling = this.getDwellingDefectIndicesByDwellingFromPayload(this.activeComp);
            const outbuildingIndices = this.getOutbuildingIndicesFromPayload(this.activeComp);
            const vehicleStorageIndices = this.getVehicleStorageIndicesFromPayload(this.activeComp);
            const amenityCollectionIndices = this.getAmenityCollectionIndicesFromPayload(this.activeComp, false);
            const siteStreetTypeIndices = this.getSiteStreetTypeIndicesFromPayload(this.activeComp);
            const propertyRestrictionIndices = this.getPropertyRestrictionIndicesFromPayload(this.activeComp);
            const easementIndices = this.getEasementIndicesFromPayload(this.activeComp);
            const encroachmentIndices = this.getEncroachmentIndicesFromPayload(this.activeComp);
            const siteFeatureIndices = this.getSiteFeatureIndicesFromPayload(this.activeComp);
            const renewableEnergyComponentIndices = this.getRenewableEnergyComponentIndicesFromPayload(this.activeComp);
            const siteInfluenceIndices = this.getSiteInfluenceIndicesFromPayload(this.activeComp);
            const viewImpairmentIndices = this.getViewImpairmentIndicesFromPayload(this.activeComp);
            const siteDrainageIndices = this.getSiteDrainageIndicesFromPayload(this.activeComp);
            const siteViewIndices = this.getSiteViewIndicesFromPayload(this.activeComp);
            const waterFrontageIndices = this.getWaterFrontageIndicesFromPayload(this.activeComp);
            const buildingCertificationIndices = this.getBuildingCertificationIndicesFromPayload(this.activeComp);
            const efficiencyRatingIndices = this.getEfficiencyRatingIndicesFromPayload(this.activeComp);
            const saleListingInformationIndices = this.getSaleListingInformationIndicesFromPayload(this.activeComp);
            const OwnersIndices = this.getOwnersIndicesFromPayload(this.activeComp);
            const assessorParcelIndices = this.getAssessorParcelIndicesFromPayload(this.activeComp);
            const transferHistoryIndices = this.getTransferHistoryIndicesFromPayload(this.activeComp);
            const ExteriorQualitiesIndices = this.getExteriorQualitiesIndicesFromPayload(this.activeComp);
            const exteriorConditionIndices = this.getExteriorConditionIndicesFromPayload(this.activeComp);
            const interiorQualityIndices = this.getInteriorQualityIndicesFromPayload(this.activeComp);
            const interiorConditionIndices = this.getInteriorConditionIndicesFromPayload(this.activeComp);
            const ADUQualitiesIndices = this.getADUQualitiesIndicesFromPayload(this.activeComp);
            const aduConditionsIndices = this.getADUConditionsIndicesFromPayload(this.activeComp);

            for (const row of rowDatas) {
                const sectionName = String(row?.sectionName || '');
                const normalizedSectionName = sectionName.toLowerCase().replace(/\s+/g, '');
                const rowFieldName = String(row?.fieldName || '');
                const isDwellingSection = normalizedSectionName.startsWith('dwell') ||
                    rowFieldName.startsWith('Dwellings-');
                const isUnitSection = normalizedSectionName.startsWith('unit');
                const isOutbuildingSection = normalizedSectionName.startsWith('outbuilding') ||
                    rowFieldName.startsWith('Outbuildings-');
                const isVehicleStorageSection = normalizedSectionName.startsWith('vehiclestorage') ||
                    rowFieldName.startsWith('VehicleStorage-');
                // "AmenityCategory" is the Subject's own generic PU_SPA_AMENITY_CATEGORY/TYPE
                // container (unrelated to the Comp's discrete OutdoorLiving/WholeHome/etc. rows).
                // isAmenitySection below matches on "AMENITY"/"SPA_AMENITY" broadly, which would
                // otherwise also catch this collection and let the Comp-oriented canonical-index
                // remapping/auto-commit logic corrupt the Subject's already-correct values
                // (confirmed: raw PR data has 2 distinct entries, but this logic overwrote index 1
                // with a duplicate of index 0).
                const isGenericSubjectAmenityCategoryCollection = /^Amenities-\d+-AmenityCategory-\d+-/.test(rowFieldName);
                const isAmenitySection =
                    !isGenericSubjectAmenityCategoryCollection &&
                    (normalizedSectionName.startsWith('propertyamenities') ||
                    rowFieldName.startsWith('Amenities-') ||
                    rowFieldName.includes('AMENITY') ||
                    rowFieldName.includes('SPA_AMENITY'));
                const isSiteStreetTypeSection =
                    rowFieldName.startsWith('SiteStreetType-') ||
                    rowFieldName === 'SIT_STREET_TYPE_SURFACE' ||
                    rowFieldName === 'PU_STREET_TYPE' ||
                    rowFieldName === 'PU_STREET_SURFACE' ||
                    rowFieldName === 'SITE_STREET_TYPE' ||
                    rowFieldName === 'PU_SITE_STREET_TYPE' ||
                    rowFieldName === 'PU_SITE_STREET_SURFACE';
                const isPropertyRestrictionsSection =
                    rowFieldName.startsWith('PropertyRestrictions-') ||
                    rowFieldName === 'SIT_FEAT_RSTRCTN_DTL';
                const isEasementsSection =
                    rowFieldName.startsWith('Easements-') ||
                    rowFieldName === 'SIT_FEAT_EASEMENT_DTL';
                const isEncroachmentsSection =
                    rowFieldName.startsWith('Encroachments-') ||
                    rowFieldName === 'SIT_FEAT_ENCROACH_DTL' ||
                    rowFieldName === 'PU_SITE_FEAT_ENCROACH' ||
                    rowFieldName === 'PU_SITE_FEAT_ENCROACH_BRD_ON_SUB' ||
                    rowFieldName === 'PU_SITE_FEAT_ENCROACH_BRD_ON_ADJ';
                const isSiteFeaturesSection =
                    rowFieldName.startsWith('SiteFeatures-') ||
                    rowFieldName === 'SIT_FEAT_SITCHAR_DTL' ||
                    rowFieldName === 'PU_SITE_FEAT_SITCHAR' ||
                    rowFieldName === 'PU_SITE_FEAT_SITCHAR_TOP_TYPE' ||
                    rowFieldName === 'PU_SITE_FEAT_SITCHAR_DRAIN_RSN';
                const isRenewableEnergyComponentsSection =
                    rowFieldName.startsWith('RenewableEnergyComponents-') ||
                    rowFieldName === 'EE_RENEWABLES' ||
                    rowFieldName === 'PU_EE_RENEWABLE_TYPE' ||
                    rowFieldName === 'PU_EE_OWNER_TYPE' ||
                    rowFieldName === 'PU_EE_FINANCING_ARRANGEMENT_YES';
                const isSiteInfluenceSection =
                    rowFieldName.startsWith('SiteInfluence-') ||
                    rowFieldName === 'SIT_INFL' ||
                    rowFieldName === 'SIT_INFL_FLATTENED' ||
                    rowFieldName === 'SIT_INFL_INFL' ||
                    rowFieldName === 'PU_SITE_INFL_WTR_DTL_TYPE' ||
                    rowFieldName === 'PU_SITE_INFL_WTR_DTL_PRIV_ACCESS';
                const isViewImpairmentSection =
                    rowFieldName.startsWith('ViewImpairment-') ||
                    rowFieldName === 'SIT_VWIMP_VIEW_FLATTENED' ||
                    rowFieldName === 'SIT_VWIMP_VIEW' ||
                    rowFieldName === 'SIT_VWIMP_RNGVW' ||
                    rowFieldName === 'SIT_VWIMP_IMP';
                const isSiteDrainageSection =
                    rowFieldName.startsWith('SiteDrainage-') ||
                    rowFieldName === 'SITE_DRAINAGE';
                const isSiteViewsSection =
                    rowFieldName.startsWith('SiteViews-') ||
                    rowFieldName === 'ST_VIEW' ||
                    rowFieldName === 'PU_ST_VIEW' ||
                    rowFieldName === 'PU_ST_RANGE';
                const isWaterFrontagesSection =
                    rowFieldName.startsWith('WaterFrontages-') ||
                    rowFieldName === 'WATER_FRONTAGE' ||
                    rowFieldName === 'PU_WATER_FRONTAGE_TYPE' ||
                    rowFieldName === 'PU_WATER_FRONTAGE_NAME' ||
                    rowFieldName === 'PU_WATER_FRONTAGE_ACCESS_DEPTH';
                const isBuildingCertificationsSection =
                    rowFieldName.startsWith('BuildingCertifications-') ||
                    rowFieldName === 'GREEN_BUILD_CERT' ||
                    rowFieldName === 'PU_GREEN_BUILD_CERT_DESC' ||
                    rowFieldName === 'PU_GREEN_BUILD_CERT_RATE' ||
                    rowFieldName === 'EE_CERTS_ORGANIZATION' ||
                    rowFieldName === 'EE_CERTS_ORGANIZATION_FLATTENED' ||
                    rowFieldName === 'EE_CERTS' ||
                    rowFieldName === 'EE_YEAR' ||
                    rowFieldName === 'EE_VERSION' ||
                    rowFieldName === 'EE_RATING';
                const isEfficiencyRatingsSection =
                    rowFieldName.startsWith('EfficiencyRatings-') ||
                    rowFieldName === 'GREEN_EFF_RATING' ||
                    rowFieldName === 'PU_GREEN_EFF_RATING' ||
                    rowFieldName === 'PU_GREEN_EFF_SCORE' ||
                    rowFieldName === 'EE_GREEN_EFF' ||
                    rowFieldName === 'EE_GREEN_EFF_FLATTENED' ||
                    rowFieldName === 'EE_GREEN_RATING' ||
                    rowFieldName === 'EE_GREEN_SCORE';
                const isSaleListingInformationSection =
                    rowFieldName.startsWith('SaleListingInformation-') ||
                    rowFieldName === 'SLI_LIST_STATUS' ||
                    rowFieldName === 'SLI_LIST_CUR_OR_FINAL_LP';
                const isOwnersSection =
                    rowFieldName.startsWith('Owners-') ||
                    this.isOwnersField(rowFieldName);
                const isAssessorParcelSection =
                    rowFieldName.startsWith('AssessorParcels-') ||
                    this.isAssessorParcelField(rowFieldName);
                const isTransferHistoriesSection =
                    rowFieldName.startsWith('TransferHistories-') ||
                    this.isTransferHistoryField(rowFieldName);
                const isExteriorQualitiesSection =
                    rowFieldName.startsWith('ExteriorQualities-') ||
                    this.isExteriorQualitiesSection(sectionName);
                const isExteriorConditionSection =
                    rowFieldName.startsWith('ExteriorConditions-') ||
                    this.isExteriorConditionSection(sectionName);
                const isInteriorQualitySection =
                    rowFieldName.startsWith('InteriorQualities-') ||
                    this.isInteriorQualitySection(sectionName);
                const isInteriorConditionSection =
                    rowFieldName.startsWith('InteriorConditions-') ||
                    this.isInteriorConditionSection(sectionName);
                const isADUQualitiesSection =
                    rowFieldName.startsWith('ADUQualities-') ||
                    this.isADUQualitiesSection(sectionName);
                const isADUConditionsSection =
                    rowFieldName.startsWith('ADUConditions-') ||
                    this.isADUConditionsSection(sectionName);
                const isSubjectNestedFlattenedField =
                    rowFieldName === 'PROJECT_TYPE' ||
                    rowFieldName === 'NATAMLAND' ||
                    rowFieldName === 'SITE_OWNED_IN_COMMON' ||
                    rowFieldName === 'HORESPMAINT' ||
                    rowFieldName === 'NEWCONST' ||
                    rowFieldName.startsWith('ProjectType-') ||
                    rowFieldName.startsWith('NativeAmericanLands-') ||
                    rowFieldName.startsWith('SiteOwnedInCommon-') ||
                    rowFieldName.startsWith('HomeownerResponsible-') ||
                    rowFieldName.startsWith('NewConstruction-');

                if (
                    !isDwellingSection &&
                    !isUnitSection &&
                    !isOutbuildingSection &&
                    !isVehicleStorageSection &&
                    !isAmenitySection &&
                    !isSiteStreetTypeSection &&
                    !isPropertyRestrictionsSection &&
                    !isEasementsSection &&
                    !isEncroachmentsSection &&
                    !isSiteFeaturesSection &&
                    !isRenewableEnergyComponentsSection &&
                    !isSiteInfluenceSection &&
                    !isViewImpairmentSection &&
                    !isSiteDrainageSection &&
                    !isSiteViewsSection &&
                    !isWaterFrontagesSection &&
                    !isBuildingCertificationsSection &&
                    !isEfficiencyRatingsSection &&
                    !isSaleListingInformationSection &&
                    !isOwnersSection &&
                    !isAssessorParcelSection &&
                    !isTransferHistoriesSection &&
                    !isExteriorQualitiesSection &&
                    !isExteriorConditionSection &&
                    !isInteriorQualitySection &&
                    !isInteriorConditionSection &&
                    !isADUQualitiesSection &&
                    !isADUConditionsSection &&
                    !isSubjectNestedFlattenedField
                ) {
                    continue;
                }

                const cells = Array.isArray(row.cells) ? row.cells : [];
                const selectedCell = cells.find((cell: any) => cell?.dataSelected && cell?.sourceInitial);
                // Non-subject comps insert an extra Subject Property column at index 1, shifting
                // Transfer to index 2 - a fixed cellIdx<=1 cutoff let the Transfer cell itself (which
                // carries the CURRENTLY STORED, possibly stale, sourceInitial/value) leak in as a fake
                // "source candidate". Exclude by each cell's stable .initials type instead of position.
                const metaColumnInitials = new Set([
                    SourceDataInitials.FieldData,
                    SourceDataInitials.SubjectProperty,
                    SourceDataInitials.TransferData,
                    SourceDataInitials.CascadeData,
                ]);
                const sourceCandidates = cells.filter((cell: any) => {
                    if (metaColumnInitials.has(cell?.initials)) {
                        return false;
                    }

                    const hasValue = cell?.value !== null && cell?.value !== undefined && String(cell.value).trim() !== '';
                    const hasSource = !!cell?.sourceInitial && String(cell.sourceInitial).trim() !== '';
                    return hasValue && hasSource && !cell?.isFakeData;
                });

                if (sourceCandidates.length === 0) {
                    continue;
                }

                const getSourcePriority = (sourceInitial: string): number => {
                    const normalizedInitial = String(sourceInitial || '').toLowerCase();
                    const idx = this.sourceColumnOrder.findIndex((setting: any) =>
                        String(setting?.initials || '').toLowerCase() === normalizedInitial
                    );
                    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
                };

                const matchedCandidates = sourceCandidates.filter((cell: any) => cell?.dataMatch);
                const selectedInitial = String(selectedCell?.sourceInitial || '').toLowerCase();
                const selectedPriority = selectedCell ? getSourcePriority(selectedCell.sourceInitial) : Number.MAX_SAFE_INTEGER;
                const hasBetterMatchedCandidate = matchedCandidates.some(
                    (cell: any) => getSourcePriority(cell.sourceInitial) < selectedPriority
                );
                const hasBetterSourceCandidate = sourceCandidates.some(
                    (cell: any) => getSourcePriority(cell.sourceInitial) < selectedPriority
                );
                const shouldResolvePrLockIn =
                    (isDwellingSection || isUnitSection || isWaterFrontagesSection || isBuildingCertificationsSection ||
                     isOutbuildingSection || isVehicleStorageSection || isAmenitySection ||
                     isSiteStreetTypeSection || isPropertyRestrictionsSection || isEasementsSection || isEncroachmentsSection ||
                     isSiteFeaturesSection ||
                     isRenewableEnergyComponentsSection ||
                     isSiteInfluenceSection || isViewImpairmentSection ||
                     isSiteDrainageSection || isSiteViewsSection || isEfficiencyRatingsSection ||
                     isSaleListingInformationSection ||
                     isOwnersSection || isAssessorParcelSection || isExteriorQualitiesSection || isExteriorConditionSection ||
                     isInteriorQualitySection || isInteriorConditionSection || isADUQualitiesSection || isADUConditionsSection ||
                     isSubjectNestedFlattenedField) &&
                    selectedCell &&
                    selectedInitial === SourceDataInitials.PublicRecords &&
                    hasBetterSourceCandidate;

                const selectedCellSourceInitialIdx =
                    selectedCell?.sourceInitialIdx === undefined || selectedCell?.sourceInitialIdx === null
                        ? undefined
                        : Number(selectedCell.sourceInitialIdx);
                // Treat any explicit selection scope as authoritative:
                // -1 = bulk "Use all data", -2 = manual main-column click, >=0 = sub-comp click.
                // Auto-ranking should only run when selection scope is implicit.
                const hasExplicitSelectionScope = selectedCellSourceInitialIdx !== undefined;

                const fallbackCell = selectedCell ?? sourceCandidates[0];

                let sourceCell = fallbackCell;
                const isAnyRepeatableSection =
                    isDwellingSection || isUnitSection || isWaterFrontagesSection || isBuildingCertificationsSection ||
                    isOutbuildingSection || isVehicleStorageSection || isAmenitySection ||
                    isSiteStreetTypeSection || isPropertyRestrictionsSection || isEasementsSection || isEncroachmentsSection ||
                    isSiteFeaturesSection ||
                    isRenewableEnergyComponentsSection ||
                    isSiteInfluenceSection || isViewImpairmentSection ||
                    isSiteDrainageSection || isSiteViewsSection || isEfficiencyRatingsSection ||
                    isSaleListingInformationSection ||
                    isOwnersSection || isAssessorParcelSection || isExteriorQualitiesSection || isExteriorConditionSection ||
                    isInteriorQualitySection || isInteriorConditionSection || isADUQualitiesSection || isADUConditionsSection ||
                    isSubjectNestedFlattenedField;
                const shouldUsePriorityRankedSelection =
                    !hasExplicitSelectionScope &&
                    (isTransferHistoriesSection ||
                    (isAnyRepeatableSection && (!selectedCell || !selectedCell.dataMatch || hasBetterMatchedCandidate || shouldResolvePrLockIn)));
                if (shouldUsePriorityRankedSelection) {
                    // matchedCandidates match against the CURRENTLY STORED transfer value, which for
                    // Amenity rows can itself be a stale/incorrect duplicate from an earlier canonical-index
                    // bug. Preferring "whatever already matches the stored value" makes a wrong value
                    // self-reinforcing (it keeps re-selecting itself as the correct pick). Amenity rows
                    // instead always rank strictly by this row's own real source-column priority.
                    const rankedSourcePool = (shouldResolvePrLockIn || isAmenitySection)
                        ? sourceCandidates
                        : (matchedCandidates.length > 0 ? matchedCandidates : sourceCandidates);
                    const rankedCandidates = rankedSourcePool
                        .slice()
                        .sort((a: any, b: any) => getSourcePriority(a.sourceInitial) - getSourcePriority(b.sourceInitial));

                    sourceCell = rankedCandidates[0];
                }

                if (!sourceCell || !sourceCell.sourceInitial) {
                    continue;
                }

                const payloadFieldName = this.getRepeatingPayloadFieldName(
                    row,
                    dwellingIndices,
                    unitIndicesByDwelling,
                    unitLevelIndicesByDwellingUnit,
                    unitRoomIndicesByDwellingUnit,
                    dwellingHeatingIndicesByDwelling,
                    dwellingCoolingIndicesByDwelling,
                    dwellingFoundationIndicesByDwelling,
                    dwellingFinishedAreaRoomSummaryIndicesByDwelling,
                    dwellingDefectIndicesByDwelling,
                    outbuildingIndices,
                    vehicleStorageIndices,
                    amenityCollectionIndices,
                    siteStreetTypeIndices,
                    propertyRestrictionIndices,
                    easementIndices,
                    encroachmentIndices,
                    siteFeatureIndices,
                    renewableEnergyComponentIndices,
                    siteInfluenceIndices,
                    viewImpairmentIndices,
                    siteDrainageIndices,
                    siteViewIndices,
                    waterFrontageIndices,
                    buildingCertificationIndices,
                    efficiencyRatingIndices,
                    saleListingInformationIndices,
                    OwnersIndices,
                    assessorParcelIndices,
                    transferHistoryIndices,
                    ExteriorQualitiesIndices,
                    exteriorConditionIndices,
                    interiorQualityIndices,
                    interiorConditionIndices,
                    ADUQualitiesIndices,
                    aduConditionsIndices
                );
                const selectedValue = sourceCell.value ?? '';
                const selectedSourceInitial = String(sourceCell.sourceInitial || '').toLowerCase();
                const selectedSourceInitialIdx = shouldUsePriorityRankedSelection
                    ? sourceCell.sourceInitialIdx
                    : (selectedCell ? sourceCell.sourceInitialIdx : undefined);
                const selectedFieldRulesApplied = !!sourceCell.fieldRulesApplied;

                const currentSourceData = this.activeComp?.sourceData?.find((item: any) => item.fieldName === payloadFieldName);

                if (isAmenitySection) {
                    // Stringified (not passed as an object) so nothing is hidden behind a
                    // collapsed console arrow - every candidate's real source/value/priority is visible.
                    const candidateSummary = sourceCandidates.map((cell: any) => ({
                        source: cell.sourceInitial,
                        value: cell.value,
                        priority: getSourcePriority(cell.sourceInitial),
                        dataMatch: cell.dataMatch,
                        dataSelected: cell.dataSelected,
                    }));
                }

                // Never overwrite a value the user has manually set.
                if (currentSourceData && String(currentSourceData.sourceInitial || '').toLowerCase() === SourceDataInitials.UserOverride) {
                    continue;
                }

                // Form Filler is the initial Transfer to Form value. Preserve it when
                // repeatable rows are rebuilt after Public Records and SmartExchange load.
                // Amenity rows are excluded: canonical row indices are recomputed by
                // getAmenityCollectionIndicesFromPayload, so an FF value saved before that
                // canonicalization existed can be a stale duplicate that must be correctable.
                if (
                    !isAmenitySection &&
                    currentSourceData &&
                    String(currentSourceData.sourceInitial || '').toLowerCase() === SourceDataInitials.FormFiller &&
                    String(currentSourceData.value ?? '').trim() !== ''
                ) {
                    continue;
                }

                // The transfer value can intentionally differ from its selected source after
                // a field rule runs. Preserve that translated value during repeatable-section
                // grid rebuilds instead of replacing it with the raw source value. Amenity rows
                // are excluded for the same reason as the FF guard above: a stale duplicate row's
                // fieldRulesApplied flag must not block correcting it to the canonical value.
                if (!isAmenitySection && currentSourceData?.fieldRulesApplied) {
                    continue;
                }

                if (
                    currentSourceData &&
                    currentSourceData.value === selectedValue &&
                    String(currentSourceData.sourceInitial || '').toLowerCase() === selectedSourceInitial &&
                    currentSourceData.sourceInitialIdx === selectedSourceInitialIdx
                ) {
                    continue;
                }

                const updatedFieldId = this.getUpdatedFieldIdForRepeatingField(row.fieldId, payloadFieldName);
                const request = newUpsertCompDataRequestByFieldName(this.activeComp, payloadFieldName);
                request.value = selectedValue;
                request.sourceInitial = selectedSourceInitial;
                request.fieldId = updatedFieldId;
                request.sourceInitialIdx = selectedSourceInitialIdx;
                request.fieldRulesApplied = selectedFieldRulesApplied;
                request.description = 'DataGrid.applyTransferToFormForRepeatingSections()';

                store.commit(Mutations.CompAddressesV36.UpsertCompSourceDataByFieldName, request);
            }
        },
        getUpdatedFieldIdForRepeatingField(fieldIdValue: number, payloadFieldName: string): number {
            // Extract base fieldId (before decimal point) in case it's already expanded
            // e.g., 921.07 → 921
            const baseFieldId = Math.floor(fieldIdValue);
            
            // Extract all numeric indices that follow dashes in the fieldName
            // e.g., "Dwellings-0-Heating-1-DWELL_HEAT" → [0, 1] → "01"
            const indices: number[] = [];
            const regex = /-(\d+)/g;
            let match;
            
            while ((match = regex.exec(payloadFieldName)) !== null) {
                indices.push(parseInt(match[1]));
            }
            
            // If no indices found, return base fieldId unchanged
            if (indices.length === 0) {
                return baseFieldId;
            }
            
            // Build decimal suffix by concatenating indices
            // For example: [0, 1] → "01", [0] → "0", [0, 0] → "00"
            const decimalSuffix = indices.map(idx => idx.toString()).join('');
            const result = parseFloat(`${baseFieldId}.${decimalSuffix}`);
            return result;
        },
        processFeedDataValue(data: any) {
            let value = data.value;
            const initials = data.initials;
            const fieldName = data.fieldName;
            const transferValue = data.transferValue;
            const transferOriginalValue = data.transferOriginalValue;
            const transferInitial = data.transferInitial;
            const transferInitialIdx = data.transferInitialIdx;
            const sourceInitialIdx = data.sourceInitialIdx;
            const isSubCompColumnData = data.isSubCompColumnData;
            const normalizedTransferInitial = String(transferInitial ?? '').trim().toLowerCase();
            const normalizedInitials = String(initials ?? '').trim().toLowerCase();
            const normalizedTransferInitialIdx =
                transferInitialIdx === undefined || transferInitialIdx === null || transferInitialIdx === ''
                    ? undefined
                    : Number(transferInitialIdx);
            const normalizedSourceInitialIdx =
                sourceInitialIdx === undefined || sourceInitialIdx === null || sourceInitialIdx === ''
                    ? undefined
                    : Number(sourceInitialIdx);

            let dataSelected = false;
            let dataMatch = false;
            value = GridColumnUtils.formatData(value, fieldName);
            const hasSourceValue = value !== null && value !== undefined && String(value).trim() !== '';

            // A matching source is selected only when that specific source cell has a value.
            // Otherwise bulk Use All can mark a blank cell selected and the repeating-section
            // synchronization will write that blank value back to the transfer column.
            if (isSubCompColumnData) {
                if (normalizedTransferInitialIdx !== undefined && normalizedTransferInitialIdx === normalizedSourceInitialIdx) {
                    dataSelected = hasSourceValue && normalizedTransferInitial === normalizedInitials;
                }
            } else {
                if (hasSourceValue && normalizedTransferInitial && normalizedInitials) {
                    // If transferInitialIdx is set, it represents an explicit selection scope:
                    // -1 => bulk main column selection
                    // -2 => manual single-cell selection in a main column
                    // >=0 => sub-comp column selection
                    if (normalizedTransferInitialIdx !== undefined) {
                        if (normalizedTransferInitialIdx === -1) {
                            dataSelected = hasSourceValue && normalizedTransferInitial === normalizedInitials;
                        } else if (normalizedTransferInitialIdx === -2) {
                            dataSelected = hasSourceValue && normalizedTransferInitial === normalizedInitials;
                        } else {
                            // When a sub-comp is explicitly selected, main-column cells should not be selected.
                            dataSelected = false;
                        }
                    } else {
                        dataSelected = hasSourceValue && normalizedTransferInitial === normalizedInitials;
                    }
                }
            }

            if (value) {
                // If transferOriginalValue is not blank then you for comparsion
                let isMatch = false;
                const formattedTransferValue = GridColumnUtils.formatData(transferValue ?? '', fieldName);
                const formattedTransferOriginalValue = GridColumnUtils.formatData(transferOriginalValue ?? '', fieldName);
                const normalizedValue = String(value).toLowerCase();

                if (formattedTransferOriginalValue !== '') {
                    isMatch = String(formattedTransferOriginalValue).toLowerCase() === normalizedValue ? true : false;
                } else {
                    isMatch = String(formattedTransferValue).toLowerCase() === normalizedValue ? true : false;
                }
                dataMatch = isMatch;
            }

            return {
                value: value,
                sourceInitial: initials,
                dataMatch: dataMatch,
                dataSelected: dataSelected,
            };
        },
        resizeWindowGrid() {
            const windowHeight = window.innerHeight;
            const windowWidth = window.innerWidth;

            const gridPosition = Utils.getElementPositionById('grid');
            const gridMarginCss = Utils.getElementStylesById('grid', 'margin-left');
            const gridMargin = Utils.convertStringToNumber(gridMarginCss) | 0;
            const guidePosition = Utils.getElementPositionById('guide');
            const footerPosition = Utils.getElementPositionById('footer');

            const gridH = windowHeight - (gridPosition.top + guidePosition.height + footerPosition.height);
            const gridW = windowWidth - gridMargin * 2;

            this.gridHeight = `${gridH}px`;
            this.gridWidth = `${gridW}px`;
        },
        // Find all repeating field variants for a given fieldName.
        // For example, for 'DWELL_HEAT', finds 'DWELL_HEAT-0', 'DWELL_HEAT-1', etc.
        findRepeatingFieldsForFieldName(activeComp: any, initials: string, fieldName: string): string[] {
            const sourceData = CompUtils.getDataSourceSourceData(activeComp, initials);
            if (!sourceData) {
                return [];
            }

            const repeatingFields: string[] = [];
            let index = 0;

            // Find all repeating variants (FIELDNAME-0, FIELDNAME-1, etc.)
            // eslint-disable-next-line
            while (true) {
                const variantFieldName = `${fieldName}-${index}`;
                if (Utils.isKeyInObject(sourceData, variantFieldName)) {
                    repeatingFields.push(variantFieldName);
                    index++;
                } else {
                    break;
                }
            }

            return repeatingFields;
        },

        // Find repeating variants for a base field using the maximum repeat index
        // from the active comp only (across configured source columns).
        findRepeatingFieldsMinDisplay(fieldName: string): string[] {
            if (!this.activeComp || !this.sourceColumnOrder || this.sourceColumnOrder.length === 0) {
                return [];
            }

            let maxRepeatingCount = 0;

            for (const setting of this.sourceColumnOrder) {
                const initials = setting.initials;
                const repeatingFields = this.findRepeatingFieldsForFieldName(this.activeComp, initials, fieldName);
                if (repeatingFields.length > maxRepeatingCount) {
                    maxRepeatingCount = repeatingFields.length;
                }
            }

            if (maxRepeatingCount === 0) {
                return [];
            }

            const repeatingFieldNames: string[] = [];
            for (let i = 0; i < maxRepeatingCount; i++) {
                repeatingFieldNames.push(`${fieldName}-${i}`);
            }

            return repeatingFieldNames;
        },

        // Legacy behavior: keep row count stable across comp tabs by using
        // the maximum repeat index found across all comps in the report.
        findRepeatingFieldsMaxDisplay(fieldName: string): string[] {
            const comps = this.store.getters[Getters.CompAddressesV36.Comps] as any[];
            if (!comps || comps.length === 0) {
                return [];
            }

            let maxRepeatingCount = 0;

            for (const comp of comps) {
                for (const setting of this.sourceColumnOrder) {
                    const initials = setting.initials;
                    const repeatingFields = this.findRepeatingFieldsForFieldName(comp, initials, fieldName);
                    if (repeatingFields.length > maxRepeatingCount) {
                        maxRepeatingCount = repeatingFields.length;
                    }
                }
            }

            if (maxRepeatingCount === 0) {
                return [];
            }

            const repeatingFieldNames: string[] = [];
            for (let i = 0; i < maxRepeatingCount; i++) {
                repeatingFieldNames.push(`${fieldName}-${i}`);
            }

            return repeatingFieldNames;
        },

        // Clone a form field and update its fieldId with decimal notation.
        // For example: fieldId 734 becomes 734.1, 734.2, etc.
        // Field name suffix must match the decimal index (e.g., FIELD-1 for .1).
        cloneFormFieldWithDecimalId(originalField: any, cloneIndex: number): any {
            const clonedField = _.cloneDeep(originalField);
            clonedField.fieldId = parseFloat(`${originalField.fieldId}.${cloneIndex}`);
            clonedField.fieldName = `${originalField.fieldName}-${cloneIndex}`;
            return clonedField;
        },

        // Process repeating fields for the current grid build only.
        // Clones the original field for each repeating variant and inserts them after the original.
        async processRepeatingFieldsAndUpdateStore(
            originalField: any,
            repeatingFieldNames: string[],
            formFields: any[]
        ): Promise<any[]> {
            if (repeatingFieldNames.length === 0) {
                return formFields;
            }

            const repeatingFieldIdPrefix = `${originalField.fieldId}.`;

            // Keep any existing cloned fields so we can preserve them while reordering.
            const existingClonedFieldsById = new Map<number, any>();
            for (const field of formFields) {
                if (String(field.fieldId).startsWith(repeatingFieldIdPrefix)) {
                    existingClonedFieldsById.set(field.fieldId, field);
                }
            }

            // Build ordered clone list by decimal fieldId (e.g., 735.1, 735.2, ...).
            const orderedClonedFields: any[] = [];
            for (let i = 1; i < repeatingFieldNames.length; i++) {
                const clonedFieldId = parseFloat(`${originalField.fieldId}.${i}`);
                const existingField = existingClonedFieldsById.get(clonedFieldId);

                if (existingField) {
                    // Keep existing field metadata, but ensure index parity:
                    // fieldId .1 -> fieldName -1, fieldId .2 -> fieldName -2, etc.
                    const normalizedField = _.cloneDeep(existingField);
                    normalizedField.fieldName = `${originalField.fieldName}-${i}`;
                    orderedClonedFields.push(normalizedField);
                } else {
                    orderedClonedFields.push(this.cloneFormFieldWithDecimalId(originalField, i));
                }
            }

            // Remove existing cloned rows so we can insert an ordered contiguous block.
            const formFieldsWithoutRepeatingClones = formFields.filter((field: any) => !String(field.fieldId).startsWith(repeatingFieldIdPrefix));

            const originalFieldIndex = formFieldsWithoutRepeatingClones.findIndex((field: any) => field.fieldId === originalField.fieldId);
            if (originalFieldIndex === -1) {
                return formFields;
            }

            const updatedFormFields = [
                ...formFieldsWithoutRepeatingClones.slice(0, originalFieldIndex + 1),
                ...orderedClonedFields,
                ...formFieldsWithoutRepeatingClones.slice(originalFieldIndex + 1),
            ];

            // Avoid unnecessary store updates when order/content is already correct.
            const isSameOrder =
                updatedFormFields.length === formFields.length &&
                updatedFormFields.every((field: any, idx: number) => field.fieldId === formFields[idx].fieldId);

            if (isSameOrder) {
                return formFields;
            }
            return updatedFormFields;
        },

        getFeedDataValue(activeComp: any, initials: string, fieldName: string): string {
            const sourceData = CompUtils.getDataSourceSourceData(activeComp, initials);
            if (!sourceData) {
                return '';
            }

            // Each source numbers its own Amenities entries independently (this source's
            // "first populated entry" might be at raw index 0 or 1). Map the requested
            // canonical index (from getAmenityCollectionIndicesFromPayload) to this specific
            // source's Nth populated entry for the same collection/field, instead of matching
            // the canonical index against this source's raw index directly.
            const amenityMatch = fieldName.match(/^Amenities-\d+-(\w+)-(\d+)-(.+)$/);
            if (amenityMatch) {
                const collectionName = amenityMatch[1];
                const canonicalIndex = parseInt(amenityMatch[2]);
                const fieldSuffix = amenityMatch[3];
                const entryRegex = new RegExp(`^Amenities-\\d+-${collectionName}-(\\d+)-${fieldSuffix}$`);

                const populatedEntries = Object.keys(sourceData)
                    .map((key) => {
                        const match = key.match(entryRegex);
                        if (!match) {
                            return null;
                        }
                        const value = sourceData[key];
                        if (value === null || value === undefined || String(value).trim() === '') {
                            return null;
                        }
                        return { rawIndex: parseInt(match[1]), value };
                    })
                    .filter((entry): entry is { rawIndex: number; value: any } => entry !== null)
                    .sort((a, b) => a.rawIndex - b.rawIndex);

                return populatedEntries[canonicalIndex] ? populatedEntries[canonicalIndex].value : '';
            }

            const preferredFieldNames = this.getPreferredFieldNameCandidates(fieldName);
            for (const preferredFieldName of preferredFieldNames) {
                if (Utils.isKeyInObject(sourceData, preferredFieldName)) {
                    return sourceData[preferredFieldName];
                }
            }

            // Subject sources do not consistently retain the same zero-index path
            // shape after parsing. Treat these as equivalent:
            // Dwellings-0-HEAT_SYSTEM_FLATTENED,
            // Dwellings-0-Heating-0-HEAT_SYSTEM_FLATTENED, and
            // Heating-0-HEAT_SYSTEM_FLATTENED.
            const normalizeZeroIndexFieldName = (value: string): string | null => {
                const indexMatches = value.match(/-(\d+)(?=-|$)/g) || [];
                if (indexMatches.some((index) => Number(index.replace('-', '')) !== 0)) {
                    return null;
                }

                return value
                    .replace(/^Dwellings-0-/, '')
                    .replace(/^(Heating|Cooling|Foundations)-0-/, '')
                    .toUpperCase();
            };
            const normalizedRequestedFieldName = normalizeZeroIndexFieldName(fieldName);
            if (normalizedRequestedFieldName) {
                const equivalentSourceFieldName = Object.keys(sourceData).find(
                    (sourceFieldName) => normalizeZeroIndexFieldName(sourceFieldName) === normalizedRequestedFieldName
                );
                if (equivalentSourceFieldName) {
                    return sourceData[equivalentSourceFieldName];
                }
            }

            return '';
        },
        getSubjectPropertyValue(payloadFieldName: string): string {
            const comps = this.store.getters[Getters.CompAddressesV36.Comps] as any[];
            const subjectComp = comps?.find(
                (comp: any) => comp?.isSubject || String(comp?.compType || '').toLowerCase() === 'subject' || comp?.compNumber === 0
            );
            const subjectSourceData = subjectComp?.sourceData;
            if (!Array.isArray(subjectSourceData)) {
                return '';
            }

            // The Subject stores Property Amenities as generic category/type pairs rather than
            // the Comp's discrete per-category fields, so this can't go through the standard
            // SubjectCompRelationship field-name mapping below.
            const amenityRowMatch = payloadFieldName.match(/^Amenities-\d+-(OutdoorAccessories|OutdoorLiving|WaterFeatures|WholeHome|Miscellaneous)-(\d+)-AMENITY_[A-Z_]+$/);
            if (amenityRowMatch) {
                return this.getSubjectAmenityValue(amenityRowMatch[1], parseInt(amenityRowMatch[2]));
            }

            const relationships = (formFieldsStoreV36.SubjectCompRelationship ?? {}) as IFieldSubjectCompRelationship;
            const baseCompFieldName = normalizeRowVisibilityKey(payloadFieldName);
            const subjectFieldMappings = relationships[payloadFieldName] ?? relationships[baseCompFieldName];

            // Re-apply whatever repeating-index prefix the comp field name has (ie. "Dwellings-0-") to each mapped subject field name.
            const lastIndex = payloadFieldName.lastIndexOf(baseCompFieldName);
            const indexPrefix = lastIndex > -1 ? payloadFieldName.substring(0, lastIndex) : '';

            const resolveSubjectValue = (subjectFieldName: string): string => {
                const candidates: string[] = [subjectFieldName];
                const baseSub = normalizeRowVisibilityKey(subjectFieldName);
                if (baseSub !== subjectFieldName) {
                    candidates.push(baseSub);
                }

                for (const candidate of candidates) {
                    const match = subjectSourceData.find((item: any) => item?.fieldName === candidate);
                    if (match && match.value !== undefined && match.value !== null && String(match.value).trim() !== '') {
                        return String(match.value);
                    }
                }

                const baseMatch = subjectSourceData.find(
                    (item: any) => normalizeRowVisibilityKey(String(item?.fieldName || '')) === baseSub
                );
                if (baseMatch && baseMatch.value !== undefined && baseMatch.value !== null) {
                    return String(baseMatch.value);
                }

                return '';
            };

            if (baseCompFieldName === 'BATHS') {
                return this.getBATHSValue(indexPrefix, resolveSubjectValue);
            }

            if (subjectFieldMappings && subjectFieldMappings.length > 0) {
                // Comp fields mapped to multiple subject fields (ie. BATHS -> TOT_BATHS_FULL, TOT_BATHS_HALF)
                // have their subject values concatenated together.
                const values = subjectFieldMappings
                    .map((mapping) => mapping?.fieldName)
                    .filter((fieldName): fieldName is string => !!fieldName)
                    .map((fieldName) => resolveSubjectValue(indexPrefix + fieldName))
                    .filter((value) => value !== '');

                if (values.length > 0) {
                    return values.join(' ');
                }
            }

            // Fall back to matching directly on the payload field name itself.
            return resolveSubjectValue(payloadFieldName) || resolveSubjectValue(baseCompFieldName);
        },
        // BATHS is a one-off method to insure correct concatenation order.
        getBATHSValue(indexPrefix: string, resolveSubjectValue: (subjectFieldName: string) => string): string {
            const values = ['TOT_BATHS_FULL', 'TOT_BATHS_HALF']
                .map((fieldName) => resolveSubjectValue(indexPrefix + fieldName))
                .filter((value) => value !== '');

            return values.join(' | ');
        },
        getTransferCompData(payloadFieldName: string, fieldId?: number): any {
            const compSourceData = this.activeComp?.sourceData;
            if (!Array.isArray(compSourceData)) {
                return null;
            }

            const directMatch = compSourceData.find((f: any) => f.fieldName === payloadFieldName);
            if (directMatch) {
                return directMatch;
            }

            // Backward-compatibility for first Transfer History group: older flows may
            // store row 0 as base keys (TRANSFER_HISTORY_*) instead of TransferHistories-0-*.
            const transferHistoryZeroMatch = payloadFieldName.match(/^TransferHistories-0-(.+)$/);
            if (transferHistoryZeroMatch) {
                const baseFieldName = transferHistoryZeroMatch[1];
                const baseMatch = compSourceData.find((f: any) => f.fieldName === baseFieldName);
                if (baseMatch) {
                    return baseMatch;
                }
            }

            // FieldId fallback: prefer non-empty entries because some flows can contain
            // placeholders and transfer rows with the same fieldId.
            if (fieldId !== undefined && fieldId !== null) {
                const sameFieldIdCandidates = compSourceData.filter((f: any) => Number(f.fieldId) === Number(fieldId));
                if (sameFieldIdCandidates.length > 0) {
                    const withSourceAndValue = sameFieldIdCandidates.find((f: any) =>
                        String(f?.sourceInitial || '').trim() !== '' && String(f?.value || '').trim() !== ''
                    );

                    if (withSourceAndValue) {
                        return withSourceAndValue;
                    }

                    const withValue = sameFieldIdCandidates.find((f: any) => String(f?.value || '').trim() !== '');
                    if (withValue) {
                        return withValue;
                    }

                    return sameFieldIdCandidates[0];
                }
            }

            return null;
        },
        getPreferredFieldNameCandidates(fieldName: string): string[] {
            const subjectFlattenedFieldCollections: Record<string, string> = {
                PROJECT_TYPE: 'ProjectType',
                NATAMLAND: 'NativeAmericanLands',
                SITE_OWNED_IN_COMMON: 'SiteOwnedInCommon',
                HORESPMAINT: 'HomeownerResponsible',
                NEWCONST: 'NewConstruction',
            };
            const flattenableFieldBaseNames = new Set([
                'HEAT_SYSTEM',
                'LEVEL_IN_UNIT',
                'ROOM_LEVEL',
                'FINISHED_AREA_TOTAL_AREA',
                'DEFECT_FEATURE',
                'EXT_WINDOW_DETAIL',
                'SIT_INFL',
                'SIT_VWIMP_VIEW',
                'EE_CERTS_ORGANIZATION',
                'EE_GREEN_EFF',
                ...Object.keys(subjectFlattenedFieldCollections),
            ]);

            const fieldNameParts = fieldName.split('-');
            const fieldBaseName = fieldNameParts[fieldNameParts.length - 1] || fieldName;
            const unflattenedFieldBaseName = fieldBaseName.replace(/_FLATTENED$/, '');
            const candidates: string[] = [];
            const addCandidate = (candidate: string): void => {
                if (candidate && !candidates.includes(candidate)) {
                    candidates.push(candidate);
                }
            };
            const addFieldNameCandidates = (candidateFieldName: string): void => {
                if (flattenableFieldBaseNames.has(unflattenedFieldBaseName) && !fieldBaseName.endsWith('_FLATTENED')) {
                    addCandidate(candidateFieldName.replace(new RegExp(`${fieldBaseName}$`), `${fieldBaseName}_FLATTENED`));
                }
                addCandidate(candidateFieldName);
            };

            addFieldNameCandidates(fieldName);

            const ownerFieldMatch = fieldName.match(/^Owners-(\d+)-(SUBCUROWNER_.+)$/);
            if (ownerFieldMatch) {
                const ownerIndex = ownerFieldMatch[1];
                const ownerFieldName = ownerFieldMatch[2];
                if (
                    ownerFieldName === 'SUBCUROWNER_FIRST_NAME' ||
                    ownerFieldName === 'SUBCUROWNER_MIDDLE_NAME' ||
                    ownerFieldName === 'SUBCUROWNER_LAST_NAME' ||
                    ownerFieldName === 'SUBCUROWNER_SUFFIX_NAME'
                ) {
                    addCandidate(`Owners-${ownerIndex}-OwnersIndividual-0-${ownerFieldName}`);
                } else if (ownerFieldName === 'SUBCUROWNER_FULL_NAME') {
                    addCandidate(`Owners-${ownerIndex}-OwnersLegalEntity-0-${ownerFieldName}`);
                }
            }

            // Subject Public Records stores these flattened display values inside
            // named objects rather than at the source-data root. Also support the
            // indexed path shape used when the parent is supplied as an array.
            const subjectCollectionName = subjectFlattenedFieldCollections[unflattenedFieldBaseName];
            if (subjectCollectionName) {
                const flattenedFieldName = `${unflattenedFieldBaseName}_FLATTENED`;
                addCandidate(`${subjectCollectionName}-${flattenedFieldName}`);
                addCandidate(`${subjectCollectionName}-0-${flattenedFieldName}`);
            }

            // Subject PR collections can be rooted at Heating-0-* while other sources
            // use the canonical Dwellings-0-Heating-0-* shape.
            const rootDwellingCollectionMatch = fieldName.match(/^Dwellings-0-(.+)$/);
            if (rootDwellingCollectionMatch) {
                addFieldNameCandidates(rootDwellingCollectionMatch[1]);

                // Subject form metadata can use a shortened dwelling path such as
                // Dwellings-0-HEAT_SYSTEM_FLATTENED, while Public Records exposes
                // the same value as Heating-0-HEAT_SYSTEM_FLATTENED.
                const shortenedDwellingFieldName = rootDwellingCollectionMatch[1];
                const rootCollectionName = shortenedDwellingFieldName.startsWith('HEAT_')
                    ? 'Heating'
                    : shortenedDwellingFieldName.startsWith('COOL_')
                        ? 'Cooling'
                        : shortenedDwellingFieldName.includes('FOUNDATION')
                            ? 'Foundations'
                            : '';
                if (rootCollectionName) {
                    addFieldNameCandidates(`${rootCollectionName}-0-${shortenedDwellingFieldName}`);
                }
            }

            // For compound indexed paths where all numeric indices are 0
            // (e.g., 'Dwellings-0-YR_BLT', 'Dwellings-0-Heating-0-DWELL_HEAT'), also try
            // the flat base field name as a fallback. Sources like PR store dwelling data as
            // flat keys ('YR_BLT') rather than compound keys ('Dwellings-0-YR_BLT').
            if (fieldName !== fieldBaseName) {
                const indexMatches = fieldName.match(/-(\d+)-/g) || [];
                const allIndicesAreZero = indexMatches.length > 0 &&
                    indexMatches.every(m => parseInt(m.replace(/-/g, '')) === 0);
                if (allIndicesAreZero) {
                    addFieldNameCandidates(fieldBaseName);
                }
            }

            return candidates;
        },
        getFeedDataPropertyValueByFieldName(activeComp: any, fieldName: string) {
            const result = computed(() =>
                store.getters[Getters.CompAddressesV36.GetCompSourceDataValueByFieldName]({
                    comp: activeComp,
                    fieldName: fieldName,
                    name: 'sourceInitialIdx',
                })
            );

            return result.value;
        },
        getFeedData(activeComp: any, initials: string) {
            const sourceProperyName = `sourceData${initials}`;
            let value: IFeedData = {
                initials: initials.toLowerCase(),
                isPeerComp: false,
                peerCount: 0,
                yourCount: 0,
                records: 0,
                reportUseCount: 0,
            };
            if (Utils.isKeyInObject(activeComp, sourceProperyName)) {
                if (!Utils.isObjectEmpty(activeComp[sourceProperyName])) {
                    value.isPeerComp = activeComp[sourceProperyName].isPeerComp;
                    value.peerCount = activeComp[sourceProperyName].peerCount;
                    value.yourCount = activeComp[sourceProperyName].yourCount;
                    value.records = activeComp[sourceProperyName].records;
                    value.reportUseCount = activeComp[sourceProperyName].reportUseCount;
                }
            }
            return value;
        },
        isFeedDataEmpty(activeComp: any, initials: string): boolean {
            const sourceData = CompUtils.getDataSourceSourceData(activeComp, initials);
            return !sourceData || Utils.isObjectEmpty(sourceData);
        },
        generateFakeData(transferValue: any): string {
            let value = transferValue;
            if (value) {
                value = Utils.ranStr(value.length, 'lower');
            } else {
                value = Utils.ranStr(7, 'lower');
            }
            return value;
        },
        async initializeTooltips() {
            BootstrapHelper.tooltipInitialize();
        },
        launchMisMatchPRAddressModal(activeComp: any) {
            this.modalPRMisMatchAddress.show = true;
            this.modalPRMisMatchAddress.activeComp = activeComp;
            this.modalPRMisMatchAddress.tabIndex = activeComp.compNumber;
        },
    },
});
